import {
  Component,
  ViewChild,
  ElementRef,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  AfterViewInit,
  Inject,
  DOCUMENT
} from '@angular/core';


import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import {
  EnhancementService,
  ImageEnhancements,
  DEFAULT_ENHANCEMENTS,
} from './enhancement.service';
import { PerspectiveService, Corner } from './perspective.service';
import { ShadowEffectsService, ShadowPreset, SHADOW_PRESETS } from './shadow-effects.service';
import { PixabayService, PixabayImage, PixabayResponse } from './pixabay.service';
import { CanvasCompositorService, BackgroundConfig, CanvasGuideConfig, CompositionPreset, COMPOSITION_PRESETS } from './canvas-compositor.service';
import { ProjectService, ProjectData, CompositionState, BackgroundState, ShadowLayerState } from './project.service';
import { BackgroundRemovalService } from './background-removal.service';
import { BriaAiService, BriaTrialStatus } from './bria-ai.service';
import { AuthService } from 'src/app/Services/auth.service';

// Hammer.js is loaded globally via angular.json scripts
declare const Hammer: any;

export interface ImageEditorResult {
  imageDataUrl: string;
  width: number;
  height: number;
  projectJson: string; // JSON string of project data for re-editing
  hasChanges: boolean; // True if user made any edits, false if just opened and saved without editing
}

@Component({
    selector: 'app-image-editor',
    imports: [FormsModule, TranslocoModule],
    templateUrl: './image-editor.component.html',
    styleUrls: ['./image-editor.component.css']
})
export class ImageEditorComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('sourceCanvas') sourceCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('previewCanvas') previewCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('projectFileInput') projectFileInput!: ElementRef<HTMLInputElement>;

  @Input() imageUrl: string = '';
  @Input() projectUrl: string = ''; // URL to saved project JSON for re-editing
  @Input() visible: boolean = false;
  @Input() productName: string = '';

  @Output() onSave = new EventEmitter<ImageEditorResult>();
  @Output() onCancel = new EventEmitter<void>();
  @Output() visibleChange = new EventEmitter<boolean>();

  // State
  isLoading = false;
  isProcessing = false;
  processingMessage = '';
  openCVLoaded = false;
  loadingOpenCV = false;

  // Active tab (kept for backwards compatibility)
  activeTab: 'perspective' | 'enhancements' | 'composition' | 'export' = 'enhancements';

  // Active accordion section for new UI
  activeAccordion: 'enhance' | 'background' | 'shadow' | 'export' | null = null;

  // Collapsible section states (for nested accordions within each panel) - EXCLUSIVE MODE: only one section open at a time
  expandedSections: Set<string> = new Set(); // Start with all sections closed for space efficiency

  // Original image
  originalImage: HTMLImageElement | null = null;
  correctedImage: HTMLImageElement | null = null;

  // Perspective correction
  perspectiveCorrectionMode = false;
  perspectiveCorrected = false;
  showAspectRatioCorrectionPanel = false; // Show dedicated panel after perspective correction
  corners: Corner[] = [];
  maxCorners = 4;
  sourceCanvasScale = 1;
  draggingCornerIndex = -1;

  // Enhancements
  enhancements: ImageEnhancements = { ...DEFAULT_ENHANCEMENTS };

  // Canvas sizing - larger to prevent cropping
  maxCanvasWidth = 1200;
  maxCanvasHeight = 800;

  // Aspect Ratio Correction
  aspectOrientation: 'landscape' | 'portrait' = 'landscape';
  selectedAspectRatio: string = '';
  aspectRatios = [
    { label: '1:1 (Square)', value: '1:1' },
    { label: '4:3 (Classic)', value: '4:3' },
    { label: '3:2 (Photo)', value: '3:2' },
    { label: '16:9 (Wide)', value: '16:9' },
    { label: '5:4 (Medium)', value: '5:4' },
    { label: '2:3 (Portrait)', value: '2:3' },
    { label: '9:16 (Mobile)', value: '9:16' },
  ];

  // Precise Measurements
  targetWidth: number = 0;
  targetHeight: number = 0;
  measurementUnit: 'in' | 'cm' | 'mm' | 'px' = 'in';
  dpi: number = 300;

  // Background
  backgroundColor: string = '#ffffff';

  // Store image before background removal (for restore)
  imageBeforeBackgroundRemoval: HTMLImageElement | null = null;

  // Compose (Crop, Rotate, Flip)
  rotation: number = 0;
  flipH: boolean = false;
  flipV: boolean = false;

  // Interactive Transform Handles
  showTransformHandles: boolean = true;
  isDragging: boolean = false;
  isResizing: boolean = false;
  isRotatingHandle: boolean = false;
  dragType: string = ''; // 'move', 'tl', 'tc', 'tr', 'ml', 'mr', 'bl', 'bc', 'br', 'rotate', 'warp-corner', 'warp-pan'
  dragStartX: number = 0;
  dragStartY: number = 0;
  initialRotation: number = 0;
  currentCursor: string = 'default';

  // Canvas image state for transforms
  imageOffsetX: number = 0;
  imageOffsetY: number = 0;
  imageScale: number = 1;
  initialScale: number = 1;
  initialOffsetX: number = 0;
  initialOffsetY: number = 0;

  // Scale X/Y separate controls
  imageScaleX: number = 100; // 10-300%
  imageScaleY: number = 100; // 10-300%
  scaleLinked: boolean = true; // Link X and Y scaling
  initialScaleX: number = 100;
  initialScaleY: number = 100;

  // Position X/Y controls (percentage 0-100)
  imagePositionX: number = 50; // Center by default
  initialPositionX: number = 50;
  initialPositionY: number = 50;
  imagePositionY: number = 50; // Center by default

  // Export area settings
  showExportBoundary: boolean = true;
  exportAreaSize: number = 1200; // Export area in pixels
  workingCanvasSize: number = 1600; // Working canvas size

  // Perspective Warp Mode (distort image by dragging corners)
  perspectiveWarpMode: boolean = false;
  warpCorners: Corner[] = [];
  warpDraggingCorner: number = -1;
  warpNeedsUpdate: boolean = false;
  warpPanStartX: number = 0;
  warpPanStartY: number = 0;
  warpPanStartCorners: Corner[] = [];

  // Background Settings
  backgroundType: 'solid' | 'gradient' | 'image' = 'solid';
  gradientColor1: string = '#ffffff';
  gradientColor2: string = '#e0e0e0';
  gradientAngle: number = 180;
  backgroundImage: HTMLImageElement | null = null;
  backgroundImageScale: number = 100;
  backgroundImagePositionX: number = 50;
  backgroundImagePositionY: number = 50;
  backgroundImageOpacity: number = 100;
  backgroundImageBlur: number = 0;
  backgroundRemoved: boolean = false;

  // Active layer selection (click to select subject or background)
  activeLayer: 'subject' | 'shadow' | 'background' = 'subject';

  // Background image enhancements (full control like main image)
  backgroundImageBrightness: number = 100;
  backgroundImageContrast: number = 100;
  backgroundImageSaturation: number = 100;
  backgroundImageTemperature: number = 0;
  backgroundImageTint: number = 0;
  backgroundImageVibrance: number = 0;
  // Levels controls
  backgroundImageHighlights: number = 0;
  backgroundImageShadows: number = 0;
  backgroundImageMidtones: number = 0;

  // Background drag state
  bgDragStartX: number = 0;
  bgDragStartY: number = 0;
  initialBgPositionX: number = 50;
  initialBgPositionY: number = 50;
  initialBgScale: number = 100;

  // Subject Layer visibility
  subjectLayerVisible: boolean = true;

  // Shadow Layer
  shadowLayerEnabled: boolean = false;
  shadowLayerPreset: string = 'none';
  shadowLayerScale: number = 100;
  shadowLayerOffsetX: number = 0;
  shadowLayerOffsetY: number = 20;
  shadowLayerOpacity: number = 50;
  shadowLayerRotation: number = 0;
  shadowLayerBlur: number = 15;
  shadowLayerShape: 'silhouette' | 'ellipse' | 'rectangle' = 'ellipse';
  shadowPresets: ShadowPreset[] = SHADOW_PRESETS;
  shadowImage: HTMLImageElement | null = null;
  // Shadow drag state
  shadowDragStartX: number = 0;
  shadowDragStartY: number = 0;
  initialShadowOffsetX: number = 0;
  initialShadowOffsetY: number = 0;

  // Canvas Guides
  showGuideGrid: boolean = false;
  showCenterLines: boolean = false;
  showRuleOfThirds: boolean = false;

  // Composition Presets
  compositionPresets: CompositionPreset[] = COMPOSITION_PRESETS;
  activePresetIndex: number = -1;

  // Pixabay Integration - SEPARATE MODAL
  showPixabayModal: boolean = false; // Changed from showPixabayPanel - now opens in separate modal
  pixabaySearchQuery: string = '';
  pixabaySearchResults: PixabayImage[] = [];
  pixabayLoading: boolean = false;
  pixabaySelectedCategory: string = '';
  pixabayCurrentPage: number = 1;
  pixabayTotalHits: number = 0;
  pixabayHasMore: boolean = false;

  // Bria.ai AI Background Integration - SEPARATE MODAL
  showBriaModal: boolean = false; // New flag for separate Bria modal
  briaScenePrompt: string = '';
  briaAddShadow: boolean = true;
  // Bria shadow configuration options
  briaShadowType: 'regular' | 'float' = 'float'; // 'float' = realistic surface shadow, 'regular' = drop shadow
  briaShadowIntensity: number = 70; // 0-100
  briaShadowBlur: number = 20; // 0-50
  briaShadowWidth: number = 0; // For float shadows, adjust ellipse width (can be negative)
  briaShadowHeight: number = 0; // For float shadows, adjust ellipse height (can be negative)
  briaGenerating: boolean = false;
  briaError: string = '';
  briaResultPreview: string = '';
  briaResultImage: HTMLImageElement | null = null;
  briaTrialStatus: BriaTrialStatus = { used: 0, limit: 10, remaining: 10, isTrialActive: true };
  briaScenePresets: { name: string; prompt: string }[] = [];

  // Render throttling - prevents multiple canvas redraws per frame
  private renderPending: boolean = false;
  private enhancementDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly ENHANCEMENT_DEBOUNCE_MS = 16; // ~60fps max

  // Hammer.js touch gesture support
  private hammer: any = null;
  private touchInitialScale: number = 100;
  private touchInitialRotation: number = 0;
  private touchInitialPositionX: number = 50;
  private touchInitialPositionY: number = 50;

  // Initialization guards - prevent race conditions on load
  private viewInitialized: boolean = false;
  private imageLoadInProgress: boolean = false;
  private lastLoadedImageUrl: string = '';
  private wasVisible: boolean = false; // Track previous visibility to detect open

  constructor(
    private enhancementService: EnhancementService,
    private perspectiveService: PerspectiveService,
    private shadowEffectsService: ShadowEffectsService,
    public pixabayService: PixabayService,
    private canvasCompositorService: CanvasCompositorService,
    private projectService: ProjectService,
    private backgroundRemovalService: BackgroundRemovalService,
    private briaAiService: BriaAiService,
    private authService: AuthService,
    private transloco: TranslocoService,
  ) {
    // Initialize Bria scene presets
    this.briaScenePresets = this.briaAiService.scenePresets;
  }

  ngOnInit() {
    // Load OpenCV in background
    this.loadOpenCV();
    // Load Bria trial status
    this.loadBriaTrialStatus();
  }

  ngAfterViewInit() {
    this.viewInitialized = true;

    // If visible on first load, reset to defaults and load image
    if (this.visible) {
      this.wasVisible = true;
      this.resetToDefaults();

      // Only load if ngOnChanges hasn't already started loading this URL
      if (this.imageUrl && !this.imageLoadInProgress && this.lastLoadedImageUrl !== this.imageUrl) {
        this.loadImage(this.imageUrl);
      }

      // Initialize Hammer.js if visible on first load
      // Use setTimeout to ensure DOM is fully rendered after *ngIf
      setTimeout(() => this.initHammerTouch(), 100);
    }
  }

  /**
   * Initialize Hammer.js touch gesture support
   */
  private initHammerTouch(): void {
    // Destroy existing instance if any
    if (this.hammer) {
      this.hammer.destroy();
      this.hammer = null;
    }

    // Check if Hammer.js is available (loaded via angular.json scripts)
    if (typeof Hammer === 'undefined') {
      console.warn('Hammer.js: Library not loaded, touch gestures disabled');
      return;
    }

    if (!this.previewCanvas?.nativeElement) {
      console.warn('Hammer.js: Canvas not available for touch initialization');
      return;
    }

    const canvas = this.previewCanvas.nativeElement;
    console.log('Hammer.js: Initializing touch support on canvas', canvas.width, 'x', canvas.height);

    // Create Hammer instance with all recognizers enabled
    this.hammer = new Hammer.Manager(canvas, {
      touchAction: 'none', // Disable browser touch handling
      recognizers: [
        [Hammer.Pan, { threshold: 10, pointers: 1 }],
        [Hammer.Pinch, { enable: true }],
        [Hammer.Rotate, { enable: true }]
      ]
    });

    // Allow pinch and rotate to work together
    const pinch = this.hammer.get('pinch');
    const rotate = this.hammer.get('rotate');
    if (pinch && rotate) {
      pinch.recognizeWith(rotate);
    }

    // Track gesture start values
    this.hammer.on('pinchstart rotatestart', () => {
      console.log('Hammer.js: Gesture start - scale:', this.imageScaleX, 'rotation:', this.rotation);
      this.touchInitialScale = (this.imageScaleX + this.imageScaleY) / 2;
      this.touchInitialRotation = this.rotation;
    });

    // Handle pinch (zoom)
    this.hammer.on('pinchmove', (ev: any) => {
      const newScale = Math.max(10, Math.min(300, this.touchInitialScale * ev.scale));
      this.imageScaleX = newScale;
      this.imageScaleY = newScale;
      this.applyEnhancementsWithBackground();
    });

    // Handle rotation
    this.hammer.on('rotatemove', (ev: any) => {
      this.rotation = this.touchInitialRotation + ev.rotation;
      // Normalize to -180 to 180
      while (this.rotation > 180) this.rotation -= 360;
      while (this.rotation < -180) this.rotation += 360;
      this.applyEnhancementsWithBackground();
    });

    // Track pan start values
    this.hammer.on('panstart', () => {
      console.log('Hammer.js: Pan start - position:', this.imagePositionX, this.imagePositionY);
      this.touchInitialPositionX = this.imagePositionX;
      this.touchInitialPositionY = this.imagePositionY;
    });

    // Handle pan (drag)
    this.hammer.on('panmove', (ev: any) => {
      // Convert pixel delta to percentage based on actual canvas size
      const canvasRect = canvas.getBoundingClientRect();
      const canvasSize = Math.max(canvasRect.width, canvasRect.height) || 800;
      const deltaXPercent = (ev.deltaX / canvasSize) * 100;
      const deltaYPercent = (ev.deltaY / canvasSize) * 100;

      this.imagePositionX = Math.max(0, Math.min(100, this.touchInitialPositionX + deltaXPercent));
      this.imagePositionY = Math.max(0, Math.min(100, this.touchInitialPositionY + deltaYPercent));
      this.applyEnhancementsWithBackground();
    });

    console.log('Hammer.js: Touch support initialized successfully');
  }

  ngOnDestroy() {
    // Clean up debounce timer to prevent memory leaks
    if (this.enhancementDebounceTimer) {
      clearTimeout(this.enhancementDebounceTimer);
      this.enhancementDebounceTimer = null;
    }

    // Clean up Hammer.js instance
    if (this.hammer) {
      this.hammer.destroy();
      this.hammer = null;
    }
  }

  /**
   * Load OpenCV.js
   */
  async loadOpenCV() {
    if (this.openCVLoaded || this.loadingOpenCV) return;

    this.loadingOpenCV = true;
    try {
      const loaded = await this.perspectiveService.loadOpenCV();
      this.openCVLoaded = loaded;
    } catch (error) {
      console.error('Failed to load OpenCV:', error);
    }
    this.loadingOpenCV = false;
  }

  /**
   * Load image from URL
   */
  async loadImage(url: string) {
    // Prevent concurrent loads of the same URL
    if (this.imageLoadInProgress || this.lastLoadedImageUrl === url) {
      return;
    }

    this.isLoading = true;
    this.imageLoadInProgress = true;

    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = url;
      });

      this.originalImage = img;
      this.correctedImage = null;
      this.perspectiveCorrected = false;
      this.perspectiveCorrectionMode = false;
      this.corners = [];
      this.enhancements = this.enhancementService.getDefaultEnhancements();
      this.lastLoadedImageUrl = url;

      // Start on Enhance tab by default (like the reference tool)
      this.activeTab = 'enhancements';
      // Show image on preview canvas since we're not in correction mode yet
      // Use requestAnimationFrame to ensure canvas is ready
      requestAnimationFrame(() => this.applyEnhancements());
    } catch (error) {
      console.error('Error loading image:', error);
    }

    this.isLoading = false;
    this.imageLoadInProgress = false;
  }

  /**
   * When imageUrl input changes
   */
  ngOnChanges() {
    // Only process if view is initialized (canvas is ready)
    if (!this.viewInitialized) return;

    // Detect when modal is opened (visibility changed from false to true)
    const justOpened = this.visible && !this.wasVisible;
    this.wasVisible = this.visible;

    if (this.visible) {
      // Reset to defaults when modal opens
      if (justOpened) {
        this.resetToDefaults();

        // Initialize Hammer.js when modal opens
        // Use setTimeout to ensure DOM is fully rendered after *ngIf
        setTimeout(() => this.initHammerTouch(), 100);
      }

      // If we have a project URL, load the full project (includes all settings and layers)
      if (this.projectUrl) {
        this.loadProjectFromUrl(this.projectUrl);
      } else if (this.imageUrl && !this.imageLoadInProgress && this.lastLoadedImageUrl !== this.imageUrl) {
        // Only load if we're not already loading and this is a new URL
        this.loadImage(this.imageUrl);
      }
    }
  }

  /**
   * Load project from URL (Cloudinary or other storage)
   */
  async loadProjectFromUrl(url: string) {
    try {
      this.isProcessing = true;
      this.processingMessage = this.transloco.translate('imageEditor.loadingProject');

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to fetch project');
      }

      const json = await response.text();
      const project = this.projectService.deserializeProject(json);
      await this.restoreProjectState(project);

      this.isProcessing = false;
      this.processingMessage = '';
    } catch (error) {
      console.error('Failed to load project:', error);
      this.isProcessing = false;
      this.processingMessage = '';
      // Fall back to loading just the image
      if (this.imageUrl) {
        this.loadImage(this.imageUrl);
      }
    }
  }

  /**
   * Restore state from project data
   */
  async restoreProjectState(project: ProjectData) {
    // Load images
    if (project.originalImage) {
      this.originalImage = await this.projectService.loadImageFromDataUrl(project.originalImage);
    }
    if (project.correctedImage) {
      this.correctedImage = await this.projectService.loadImageFromDataUrl(project.correctedImage);
    }

    // Restore state flags
    this.perspectiveCorrected = project.perspectiveCorrected;
    this.backgroundRemoved = project.backgroundRemoved;

    // Restore enhancements
    this.enhancements = { ...project.enhancements };

    // Restore composition
    this.rotation = project.composition.rotation;
    this.flipH = project.composition.flipH;
    this.flipV = project.composition.flipV;
    this.imagePositionX = project.composition.imagePositionX;
    this.imagePositionY = project.composition.imagePositionY;
    this.imageScale = project.composition.imageScale;

    // Restore background
    this.backgroundType = project.background.type;
    this.backgroundColor = project.background.color;
    this.gradientColor1 = project.background.gradientColor1;
    this.gradientColor2 = project.background.gradientColor2;
    this.gradientAngle = project.background.gradientAngle;
    this.backgroundImageScale = project.background.imageScale;
    this.backgroundImagePositionX = project.background.imagePositionX;
    this.backgroundImagePositionY = project.background.imagePositionY;
    this.backgroundImageOpacity = project.background.imageOpacity;
    this.backgroundImageBlur = project.background.imageBlur;

    // Load background image if present
    if (project.background.imageDataUrl) {
      this.backgroundImage = await this.projectService.loadImageFromDataUrl(project.background.imageDataUrl);
    }

    // Restore shadow layer
    this.shadowLayerEnabled = project.shadowLayer.enabled;
    this.shadowLayerPreset = project.shadowLayer.presetId;
    this.shadowLayerScale = project.shadowLayer.scale;
    this.shadowLayerOffsetX = project.shadowLayer.offsetX;
    this.shadowLayerOffsetY = project.shadowLayer.offsetY;
    this.shadowLayerOpacity = project.shadowLayer.opacity;
    this.shadowLayerRotation = project.shadowLayer.rotation;
    if (project.shadowLayer.blur !== undefined) {
      this.shadowLayerBlur = project.shadowLayer.blur;
    }
    if (project.shadowLayer.shape) {
      this.shadowLayerShape = project.shadowLayer.shape;
    }

    // Generate shadow if enabled
    if (this.shadowLayerEnabled) {
      this.generateShadowForShape();
    }

    // Render the preview
    this.applyEnhancementsWithBackground();
  }

  // ============ PERSPECTIVE CORRECTION ============

  /**
   * Start perspective correction mode
   */
  startPerspectiveCorrection() {
    this.perspectiveCorrectionMode = true;
    this.corners = [];
    this.activeTab = 'perspective';
    this.activeAccordion = null; // Close accordion when in perspective mode
    setTimeout(() => this.drawSourceImage(), 100);
  }

  /**
   * Draw source image on canvas
   */
  drawSourceImage() {
    if (!this.sourceCanvas || !this.originalImage) return;

    const canvas = this.sourceCanvas.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = this.originalImage;

    // Calculate scale to fit canvas
    const scaleX = this.maxCanvasWidth / img.width;
    const scaleY = this.maxCanvasHeight / img.height;
    const scale = Math.min(scaleX, scaleY, 1);

    // Set canvas size
    canvas.width = img.width * scale;
    canvas.height = img.height * scale;

    // Store scale for corner conversion
    this.sourceCanvasScale = 1 / scale;

    // Draw image
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    // Draw corners if any
    if (this.corners.length > 0) {
      this.perspectiveService.drawCorners(
        ctx,
        this.corners,
        this.draggingCornerIndex,
      );
    }

    // Draw instructions
    if (this.corners.length < 4) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      ctx.fillRect(10, 10, 280, 30);
      ctx.fillStyle = '#ffffff';
      ctx.font = '14px Arial';
      ctx.fillText(
        `Click corner ${this.corners.length + 1} of 4 (clockwise from top-left)`,
        20,
        30,
      );
    }
  }

  /**
   * Handle canvas click for corner selection
   */
  onCanvasClick(event: MouseEvent) {
    if (!this.perspectiveCorrectionMode) return;

    const canvas = this.sourceCanvas.nativeElement;
    const rect = canvas.getBoundingClientRect();

    // Scale from display space to canvas space
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const x = (event.clientX - rect.left) * scaleX;
    const y = (event.clientY - rect.top) * scaleY;

    // Check if clicking on existing corner (for dragging)
    const existingCornerIndex = this.perspectiveService.findCornerAtPosition(
      this.corners,
      x,
      y,
      20,
    );

    if (existingCornerIndex >= 0) {
      // Start dragging this corner
      this.draggingCornerIndex = existingCornerIndex;
      return;
    }

    // Add new corner if less than 4
    if (this.corners.length < this.maxCorners) {
      this.corners.push({ x, y });
      this.drawSourceImage();
    }
  }

  /**
   * Handle mouse move for corner dragging
   */
  onCanvasMouseMove(event: MouseEvent) {
    if (this.draggingCornerIndex < 0) return;

    const canvas = this.sourceCanvas.nativeElement;
    const rect = canvas.getBoundingClientRect();

    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const x = (event.clientX - rect.left) * scaleX;
    const y = (event.clientY - rect.top) * scaleY;

    // Update corner position
    this.corners[this.draggingCornerIndex] = { x, y };
    this.drawSourceImage();
  }

  /**
   * Handle mouse up to stop dragging
   */
  onCanvasMouseUp() {
    this.draggingCornerIndex = -1;
  }

  /**
   * Reset corners
   */
  resetCorners() {
    this.corners = [];
    this.drawSourceImage();
  }

  /**
   * Apply perspective correction
   */
  async applyPerspectiveCorrection() {
    if (this.corners.length !== 4 || !this.originalImage) return;

    if (!this.openCVLoaded) {
      this.processingMessage = this.transloco.translate('imageEditor.loadingOpenCV');
      this.isProcessing = true;
      await this.loadOpenCV();
      if (!this.openCVLoaded) {
        this.isProcessing = false;
        alert(this.transloco.translate('imageEditor.failedToLoadOpenCV'));
        return;
      }
    }

    this.isProcessing = true;
    this.processingMessage = this.transloco.translate('imageEditor.applyingPerspectiveCorrection');

    try {
      const result = await this.perspectiveService.applyPerspectiveCorrection(
        this.originalImage,
        this.corners,
        this.sourceCanvasScale,
      );

      this.correctedImage = result.image;
      this.perspectiveCorrected = true;
      this.perspectiveCorrectionMode = false;

      // Show the aspect ratio correction panel instead of going to enhancements
      this.showAspectRatioCorrectionPanel = true;
      this.activeAccordion = null; // Hide accordion panels
      this.applyEnhancementsWithBackground();
    } catch (error) {
      console.error('Perspective correction failed:', error);
      alert(this.transloco.translate('imageEditor.perspectiveCorrectionFailed'));
    }

    this.isProcessing = false;
  }

  /**
   * Skip perspective correction
   */
  skipPerspectiveCorrection() {
    this.perspectiveCorrectionMode = false;
    this.correctedImage = this.originalImage;
    this.activeTab = 'enhancements';
    this.applyEnhancements();
  }

  // ============ ASPECT RATIO CORRECTION ============

  /**
   * Select an aspect ratio
   */
  selectAspectRatio(ratio: string) {
    this.selectedAspectRatio = ratio;
  }

  /**
   * Set aspect orientation (landscape or portrait)
   */
  setAspectOrientation(orientation: 'landscape' | 'portrait') {
    this.aspectOrientation = orientation;
    // Re-apply if a ratio is already selected
    if (this.selectedAspectRatio) {
      this.applyAspectRatioPreset(this.selectedAspectRatio);
    }
  }

  /**
   * Apply aspect ratio preset using non-destructive scaling
   * This adjusts scaleX/scaleY to achieve the target aspect ratio
   */
  applyAspectRatioPreset(ratioValue: string) {
    if (!this.originalImage && !this.correctedImage) return;

    const img = this.correctedImage || this.originalImage;
    if (!img) return;

    this.selectedAspectRatio = ratioValue;

    const [ratioW, ratioH] = ratioValue.split(':').map(Number);

    // Swap for portrait orientation
    let effectiveW = ratioW;
    let effectiveH = ratioH;
    if (this.aspectOrientation === 'portrait' && ratioW !== ratioH) {
      effectiveW = ratioH;
      effectiveH = ratioW;
    }

    const targetRatio = effectiveW / effectiveH;
    const currentRatio = img.width / img.height;

    // Calculate scale adjustments to achieve target ratio
    if (currentRatio > targetRatio) {
      // Image is wider than target - reduce width scale
      this.imageScaleX = Math.round((targetRatio / currentRatio) * 100);
      this.imageScaleY = 100;
    } else if (currentRatio < targetRatio) {
      // Image is taller than target - reduce height scale
      this.imageScaleX = 100;
      this.imageScaleY = Math.round((currentRatio / targetRatio) * 100);
    } else {
      // Already matches target ratio
      this.imageScaleX = 100;
      this.imageScaleY = 100;
    }

    this.applyEnhancementsWithBackground();
  }

  /**
   * Reset aspect ratio to original (restore 1:1 scaling)
   */
  resetAspectRatio() {
    this.selectedAspectRatio = '';
    this.imageScaleX = 100;
    this.imageScaleY = 100;
    this.applyEnhancementsWithBackground();
  }

  /**
   * Dismiss the aspect ratio correction panel and continue to main editor
   */
  dismissAspectRatioPanel() {
    this.showAspectRatioCorrectionPanel = false;
    this.activeAccordion = 'enhance'; // Open the enhance panel
  }

  /**
   * Apply aspect ratio correction to the image (legacy - crops image)
   * @deprecated Use applyAspectRatioPreset for non-destructive scaling
   */
  applyAspectRatio() {
    if (!this.selectedAspectRatio || !this.originalImage) return;

    const img = this.correctedImage || this.originalImage;
    const [ratioW, ratioH] = this.selectedAspectRatio.split(':').map(Number);

    let targetRatio = ratioW / ratioH;
    if (this.aspectOrientation === 'portrait') {
      targetRatio = ratioH / ratioW;
    }

    const currentRatio = img.width / img.height;

    let newWidth = img.width;
    let newHeight = img.height;

    if (currentRatio > targetRatio) {
      // Image is wider than target, crop width
      newWidth = Math.round(img.height * targetRatio);
    } else {
      // Image is taller than target, crop height
      newHeight = Math.round(img.width / targetRatio);
    }

    // Create cropped canvas
    const canvas = document.createElement('canvas');
    canvas.width = newWidth;
    canvas.height = newHeight;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    // Center crop
    const offsetX = (img.width - newWidth) / 2;
    const offsetY = (img.height - newHeight) / 2;

    ctx.drawImage(
      img,
      offsetX,
      offsetY,
      newWidth,
      newHeight,
      0,
      0,
      newWidth,
      newHeight,
    );

    // Create new image from canvas
    const newImg = new Image();
    newImg.src = canvas.toDataURL('image/png');
    newImg.onload = () => {
      this.correctedImage = newImg;
      this.applyEnhancements();
    };
  }

  // ============ PRECISE MEASUREMENTS ============

  /**
   * Convert measurement to pixels based on unit and DPI
   */
  convertToPixels(value: number, unit: string): number {
    switch (unit) {
      case 'in':
        return Math.round(value * this.dpi);
      case 'cm':
        return Math.round((value / 2.54) * this.dpi);
      case 'mm':
        return Math.round((value / 25.4) * this.dpi);
      case 'px':
      default:
        return Math.round(value);
    }
  }

  /**
   * Apply precise measurements to resize the image
   */
  applyMeasurements() {
    if (!this.targetWidth || !this.targetHeight || !this.originalImage) return;

    const img = this.correctedImage || this.originalImage;

    const targetWidthPx = this.convertToPixels(
      this.targetWidth,
      this.measurementUnit,
    );
    const targetHeightPx = this.convertToPixels(
      this.targetHeight,
      this.measurementUnit,
    );

    // Create resized canvas
    const canvas = document.createElement('canvas');
    canvas.width = targetWidthPx;
    canvas.height = targetHeightPx;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    // Use high quality scaling
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(img, 0, 0, targetWidthPx, targetHeightPx);

    // Create new image from canvas
    const newImg = new Image();
    newImg.src = canvas.toDataURL('image/png');
    newImg.onload = () => {
      this.correctedImage = newImg;
      this.applyEnhancements();
    };
  }

  // ============ ENHANCEMENTS ============

  /**
   * Apply enhancements to preview
   */
  applyEnhancements() {
    if (!this.previewCanvas || (!this.correctedImage && !this.originalImage))
      return;

    const canvas = this.previewCanvas.nativeElement;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    const img = this.correctedImage || this.originalImage!;

    // Calculate scale to fit canvas
    const scaleX = this.maxCanvasWidth / img.width;
    const scaleY = this.maxCanvasHeight / img.height;
    const scale = Math.min(scaleX, scaleY, 1);

    canvas.width = img.width * scale;
    canvas.height = img.height * scale;

    // Store canvas scale for coordinate conversion
    this.imageScale = scale;

    // Apply CSS filters
    ctx.filter = this.enhancementService.buildFilterString(this.enhancements);
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Apply rotation and flip transforms
    ctx.save();
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((this.rotation * Math.PI) / 180);
    ctx.scale(this.flipH ? -1 : 1, this.flipV ? -1 : 1);
    ctx.translate(-canvas.width / 2, -canvas.height / 2);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    ctx.restore();

    // Reset filter and apply pixel-level adjustments
    ctx.filter = 'none';
    this.enhancementService.applyPixelAdjustments(
      ctx,
      canvas.width,
      canvas.height,
      this.enhancements,
    );

    // Draw warp corners if in warp mode
    if (this.perspectiveWarpMode && this.warpCorners.length === 4) {
      this.perspectiveService.drawWarpCorners(
        ctx,
        this.warpCorners,
        this.warpDraggingCorner,
        20,
      );
    }
    // Draw transform handles when editing and not in warp mode or perspective correction mode
    else if (this.showTransformHandles && !this.perspectiveCorrectionMode) {
      this.perspectiveService.drawTransformHandles(
        ctx,
        0,
        0,
        canvas.width,
        canvas.height,
        this.rotation,
        20,
      );
    }
  }

  /**
   * Reset enhancements
   */
  resetEnhancements() {
    this.enhancements = this.enhancementService.getDefaultEnhancements();
    this.applyEnhancementsWithBackground();
  }

  /**
   * Reset all editor state to defaults
   * Called when opening the modal for a new image
   */
  resetToDefaults() {
    // Clear images
    this.originalImage = null;
    this.correctedImage = null;
    this.imageBeforeBackgroundRemoval = null;

    // Reset UI state
    this.activeTab = 'enhancements';
    this.activeAccordion = null;
    this.expandedSections = new Set(['transform', 'presets']);

    // Reset perspective correction
    this.perspectiveCorrectionMode = false;
    this.perspectiveCorrected = false;
    this.corners = [];
    this.draggingCornerIndex = -1;

    // Reset perspective warp
    this.perspectiveWarpMode = false;
    this.warpCorners = [];
    this.warpDraggingCorner = -1;
    this.warpNeedsUpdate = false;

    // Reset enhancements
    this.enhancements = this.enhancementService.getDefaultEnhancements();

    // Reset transform
    this.rotation = 0;
    this.flipH = false;
    this.flipV = false;
    this.imageScaleX = 100;
    this.imageScaleY = 100;
    this.scaleLinked = true;
    this.imagePositionX = 50;
    this.imagePositionY = 50;
    this.imageScale = 1;
    this.imageOffsetX = 0;
    this.imageOffsetY = 0;

    // Reset background
    this.backgroundType = 'solid';
    this.backgroundColor = '#ffffff';
    this.gradientColor1 = '#ffffff';
    this.gradientColor2 = '#e0e0e0';
    this.gradientAngle = 180;
    this.backgroundImage = null;
    this.backgroundImageScale = 100;
    this.backgroundImagePositionX = 50;
    this.backgroundImagePositionY = 50;
    this.backgroundImageOpacity = 100;
    this.backgroundImageBlur = 0;
    this.backgroundRemoved = false;

    // Reset background image enhancements
    this.backgroundImageBrightness = 100;
    this.backgroundImageContrast = 100;
    this.backgroundImageSaturation = 100;
    this.backgroundImageTemperature = 0;
    this.backgroundImageTint = 0;
    this.backgroundImageVibrance = 0;
    this.backgroundImageHighlights = 0;
    this.backgroundImageShadows = 0;
    this.backgroundImageMidtones = 0;

    // Reset layer visibility
    this.subjectLayerVisible = true;
    this.activeLayer = 'subject';

    // Reset shadow layer
    this.shadowLayerEnabled = false;
    this.shadowLayerPreset = 'none';
    this.shadowLayerScale = 100;
    this.shadowLayerOffsetX = 0;
    this.shadowLayerOffsetY = 20;
    this.shadowLayerOpacity = 50;
    this.shadowLayerRotation = 0;
    this.shadowLayerBlur = 15;
    this.shadowLayerShape = 'ellipse';
    this.shadowImage = null;

    // Reset canvas guides
    this.showGuideGrid = false;
    this.showCenterLines = false;
    this.showRuleOfThirds = false;
    this.showTransformHandles = true;
    this.activePresetIndex = -1;

    // Reset Pixabay
    this.showPixabayModal = false;
    this.pixabaySearchQuery = '';
    this.pixabaySearchResults = [];
    this.pixabayLoading = false;
    this.pixabaySelectedCategory = '';
    this.pixabayCurrentPage = 1;
    this.pixabayTotalHits = 0;
    this.pixabayHasMore = false;

    // Reset Bria AI background
    this.briaScenePrompt = '';
    this.briaAddShadow = true;
    this.briaShadowType = 'float';
    this.briaShadowIntensity = 70;
    this.briaShadowBlur = 20;
    this.briaShadowWidth = 0;
    this.briaShadowHeight = 0;
    this.briaGenerating = false;
    this.briaError = '';
    this.briaResultPreview = '';
    this.briaResultImage = null;

    // Reset drag state
    this.isDragging = false;
    this.isResizing = false;
    this.isRotatingHandle = false;
    this.dragType = '';
    this.currentCursor = 'default';

    // Reset aspect ratio
    this.selectedAspectRatio = '';
    this.aspectOrientation = 'landscape';

    // Reset processing state
    this.isLoading = false;
    this.isProcessing = false;
    this.processingMessage = '';

    // Reset image load tracking (allow reloading the same URL if it was reset)
    this.lastLoadedImageUrl = '';
  }

  /**
   * Called when any enhancement slider changes
   */
  onEnhancementChange() {
    // Debounce enhancement changes to prevent excessive renders during slider drag
    if (this.enhancementDebounceTimer) {
      clearTimeout(this.enhancementDebounceTimer);
    }
    this.enhancementDebounceTimer = setTimeout(() => {
      this.scheduleRender();
    }, this.ENHANCEMENT_DEBOUNCE_MS);
  }

  /**
   * Schedule a render using requestAnimationFrame to coalesce multiple render requests
   * This prevents the UI from freezing when sliders are dragged rapidly
   */
  private scheduleRender() {
    if (this.renderPending) return;
    this.renderPending = true;
    requestAnimationFrame(() => {
      this.renderPending = false;
      this.applyEnhancementsWithBackground();
    });
  }

  // ============ EXPORT ============

  /**
   * Export final image
   */
  exportImage(): string {
    const img = this.correctedImage || this.originalImage;
    if (!img) return '';

    // Export at 1200x1200 resolution (the export area size)
    const exportSize = 1200;

    // Create export canvas
    const canvas = document.createElement('canvas');
    canvas.width = exportSize;
    canvas.height = exportSize;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return '';

    // Calculate image dimensions to fit within export canvas with margin for rotation
    const marginFactor = 0.65; // Same as preview
    const availableSize = exportSize * marginFactor;
    const imgAspect = img.width / img.height;

    let baseImageWidth: number;
    let baseImageHeight: number;

    if (imgAspect > 1) {
      baseImageWidth = availableSize;
      baseImageHeight = availableSize / imgAspect;
    } else {
      baseImageHeight = availableSize;
      baseImageWidth = availableSize * imgAspect;
    }

    // Draw background on the entire canvas
    const bgConfig: BackgroundConfig = {
      type: this.backgroundType,
      color: this.backgroundColor || '#ffffff',
      gradientColor1: this.gradientColor1,
      gradientColor2: this.gradientColor2,
      gradientAngle: this.gradientAngle,
      image: this.backgroundImage,
      imageScale: this.backgroundImageScale,
      imagePositionX: this.backgroundImagePositionX,
      imagePositionY: this.backgroundImagePositionY,
      imageOpacity: this.backgroundImageOpacity,
      imageBlur: this.backgroundImageBlur,
      imageBrightness: this.backgroundImageBrightness,
      imageContrast: this.backgroundImageContrast,
      imageSaturation: this.backgroundImageSaturation,
      imageTemperature: this.backgroundImageTemperature,
      imageTint: this.backgroundImageTint,
      imageHighlights: this.backgroundImageHighlights,
      imageShadows: this.backgroundImageShadows,
      imageMidtones: this.backgroundImageMidtones
    };
    this.canvasCompositorService.drawBackground(ctx, exportSize, exportSize, bgConfig);

    // Draw shadow layer if enabled
    if (this.shadowLayerEnabled && this.shadowImage) {
      const shadowConfig = {
        enabled: true,
        image: this.shadowImage,
        scale: this.shadowLayerScale,
        offsetX: this.shadowLayerOffsetX, // Offset is already in export-area pixels
        offsetY: this.shadowLayerOffsetY,
        opacity: this.shadowLayerOpacity,
        rotation: this.shadowLayerRotation,
        blur: this.shadowLayerBlur
      };
      this.canvasCompositorService.drawShadowLayer(
        ctx,
        shadowConfig,
        exportSize / 2,
        exportSize / 2,
        baseImageWidth * 0.8
      );
    }

    // Only draw subject layer if visible (hidden when AI background already contains the product)
    if (this.subjectLayerVisible) {
      // Apply CSS filters and draw the image
      ctx.filter = this.enhancementService.buildFilterString(this.enhancements);

      // Calculate image dimensions with user scale adjustments
      const imgScaleX = this.imageScaleX / 100;
      const imgScaleY = this.imageScaleY / 100;
      const scaledWidth = baseImageWidth * imgScaleX;
      const scaledHeight = baseImageHeight * imgScaleY;

      // Calculate position based on percentage within the export canvas
      const posX = (this.imagePositionX / 100) * exportSize;
      const posY = (this.imagePositionY / 100) * exportSize;

      ctx.save();
      // Move to position
      ctx.translate(posX, posY);
      // Apply rotation
      ctx.rotate((this.rotation * Math.PI) / 180);
      // Apply flip
      ctx.scale(this.flipH ? -1 : 1, this.flipV ? -1 : 1);
      // Draw image centered at position with scale
      ctx.drawImage(img, -scaledWidth / 2, -scaledHeight / 2, scaledWidth, scaledHeight);
      ctx.restore();

      ctx.filter = 'none';
      // Apply pixel adjustments
      this.enhancementService.applyPixelAdjustments(ctx, exportSize, exportSize, this.enhancements);
    }

    return canvas.toDataURL('image/png');
  }

  /**
   * Check if any edits have been made to the image
   * Used to determine if we should return original image or exported canvas
   */
  hasChanges(): boolean {
    // Check if enhancements differ from defaults
    const defaultEnhancements = { ...DEFAULT_ENHANCEMENTS };
    const enhancementsChanged =
      this.enhancements.brightness !== defaultEnhancements.brightness ||
      this.enhancements.contrast !== defaultEnhancements.contrast ||
      this.enhancements.saturation !== defaultEnhancements.saturation ||
      this.enhancements.blur !== defaultEnhancements.blur ||
      this.enhancements.grayscale !== defaultEnhancements.grayscale ||
      this.enhancements.sepia !== defaultEnhancements.sepia ||
      this.enhancements.temperature !== defaultEnhancements.temperature ||
      this.enhancements.tint !== defaultEnhancements.tint ||
      this.enhancements.vibrance !== defaultEnhancements.vibrance ||
      this.enhancements.highlights !== defaultEnhancements.highlights ||
      this.enhancements.shadows !== defaultEnhancements.shadows ||
      this.enhancements.midtones !== defaultEnhancements.midtones ||
      this.enhancements.sharpen !== defaultEnhancements.sharpen ||
      this.enhancements.denoise !== defaultEnhancements.denoise;

    // Check transforms
    const transformsChanged =
      this.rotation !== 0 ||
      this.flipH !== false ||
      this.flipV !== false ||
      this.imageScaleX !== 100 ||
      this.imageScaleY !== 100 ||
      this.imagePositionX !== 50 ||
      this.imagePositionY !== 50;

    // Check background changes
    const backgroundChanged =
      this.backgroundRemoved ||
      this.backgroundType !== 'solid' ||
      this.backgroundColor !== '#ffffff' ||
      this.backgroundImage !== null;

    // Check perspective correction
    const perspectiveChanged = this.perspectiveCorrected || this.correctedImage !== null;

    // Check shadow layer
    const shadowChanged = this.shadowLayerEnabled;

    return enhancementsChanged || transformsChanged || backgroundChanged || perspectiveChanged || shadowChanged;
  }

  /**
   * Save and close
   */
  save() {
    // If no changes were made and we have the original image
    // Just emit with hasChanges: false so parent knows not to create duplicate
    if (!this.hasChanges() && this.originalImage) {
      const projectData = this.createCurrentProjectData();
      const projectJson = this.projectService.serializeProject(projectData);

      // Use original image dimensions
      const originalWidth = this.originalImage.width;
      const originalHeight = this.originalImage.height;

      // Convert original image to data URL at full resolution
      const canvas = document.createElement('canvas');
      canvas.width = originalWidth;
      canvas.height = originalHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(this.originalImage, 0, 0, originalWidth, originalHeight);
        const dataUrl = canvas.toDataURL('image/png');

        this.onSave.emit({
          imageDataUrl: dataUrl,
          width: originalWidth,
          height: originalHeight,
          projectJson: projectJson,
          hasChanges: false, // No edits made - don't create duplicate in additional images
        });
      }

      this.close();
      return;
    }

    // Changes were made - export the composed image
    const dataUrl = this.exportImage();

    if (dataUrl) {
      // Create project data for re-editing
      const projectData = this.createCurrentProjectData();
      const projectJson = this.projectService.serializeProject(projectData);

      // Export is always 1200x1200 (the composed canvas size)
      this.onSave.emit({
        imageDataUrl: dataUrl,
        width: 1200,
        height: 1200,
        projectJson: projectJson,
        hasChanges: true, // User made edits - save as new image
      });
    }

    this.close();
  }

  /**
   * Create project data from current state
   */
  createCurrentProjectData(): ProjectData {
    const composition: CompositionState = {
      rotation: this.rotation,
      flipH: this.flipH,
      flipV: this.flipV,
      imagePositionX: this.imagePositionX,
      imagePositionY: this.imagePositionY,
      imageScale: this.imageScale,
    };

    const background: BackgroundState = {
      type: this.backgroundType,
      color: this.backgroundColor,
      gradientColor1: this.gradientColor1,
      gradientColor2: this.gradientColor2,
      gradientAngle: this.gradientAngle,
      imageDataUrl: this.backgroundImage ? this.imageToDataUrl(this.backgroundImage) : null,
      imageScale: this.backgroundImageScale,
      imagePositionX: this.backgroundImagePositionX,
      imagePositionY: this.backgroundImagePositionY,
      imageOpacity: this.backgroundImageOpacity,
      imageBlur: this.backgroundImageBlur,
    };

    const shadowLayer: ShadowLayerState = {
      enabled: this.shadowLayerEnabled,
      presetId: this.shadowLayerPreset,
      scale: this.shadowLayerScale,
      offsetX: this.shadowLayerOffsetX,
      offsetY: this.shadowLayerOffsetY,
      opacity: this.shadowLayerOpacity,
      rotation: this.shadowLayerRotation,
      blur: this.shadowLayerBlur,
      shape: this.shadowLayerShape,
    };

    return this.projectService.createProjectData(
      this.originalImage,
      this.correctedImage,
      this.perspectiveCorrected,
      this.backgroundRemoved,
      this.enhancements,
      composition,
      background,
      shadowLayer
    );
  }

  /**
   * Cancel and close
   */
  cancel() {
    this.onCancel.emit();
    this.close();
  }

  /**
   * Close dialog - always reset all state so dialog opens fresh next time
   */
  close() {
    this.resetAllState();
    this.originalImage = null;
    this.correctedImage = null;
    this.visible = false;
    this.wasVisible = false; // Reset so next open triggers resetToDefaults
    this.visibleChange.emit(false);
  }

  /**
   * Set active tab
   */
  setActiveTab(tab: 'perspective' | 'enhancements' | 'composition' | 'export') {
    // Exit perspective mode when switching to other tabs
    if (tab !== 'perspective' && this.perspectiveCorrectionMode) {
      this.perspectiveCorrectionMode = false;
      this.corners = [];
    }

    this.activeTab = tab;

    if (tab === 'perspective' && this.originalImage) {
      setTimeout(() => this.drawSourceImage(), 50);
    } else if (this.correctedImage || this.originalImage) {
      // Use applyEnhancementsWithBackground for all tabs except perspective
      // to show the full composite view with background
      setTimeout(() => this.applyEnhancementsWithBackground(), 50);
    }
  }

  /**
   * Switch tab - allows free navigation between all tabs
   */
  switchTab(tab: 'perspective' | 'enhancements' | 'composition' | 'export') {
    this.setActiveTab(tab);
  }

  /**
   * Toggle accordion section open/closed
   */
  toggleAccordion(section: 'enhance' | 'background' | 'shadow' | 'export') {
    if (this.activeAccordion === section) {
      this.activeAccordion = null;
    } else {
      this.activeAccordion = section;
      // Auto-select appropriate layer when opening certain accordions
      if (section === 'shadow') {
        this.selectShadowLayer();
      } else if (section === 'background') {
        this.selectBackgroundLayer();
      } else if (section === 'enhance') {
        this.selectSubjectLayer();
      }
    }
  }

  /**
   * Toggle a collapsible section within a panel (exclusive - only one open at a time)
   */
  toggleSection(sectionId: string): void {
    if (this.expandedSections.has(sectionId)) {
      // Clicking the open section closes it
      this.expandedSections.delete(sectionId);
    } else {
      // Close all other sections and open this one
      this.expandedSections.clear();
      this.expandedSections.add(sectionId);
      // Auto-scroll to AI Background section when expanded
      if (sectionId === 'ai-background') {
        setTimeout(() => {
          const accordionContent = document.querySelector('.accordion-content');
          if (accordionContent) {
            // Scroll to bottom to show AI Background section fully
            accordionContent.scrollTop = accordionContent.scrollHeight;
          }
        }, 150);
      }
    }
  }

  /**
   * Check if a collapsible section is expanded
   */
  isSectionExpanded(sectionId: string): boolean {
    return this.expandedSections.has(sectionId);
  }

  /**
   * Cancel perspective correction mode
   */
  cancelPerspectiveCorrection() {
    this.perspectiveCorrectionMode = false;
    this.corners = [];

    // If we have corrected image, show it, otherwise show original
    if (this.correctedImage || this.originalImage) {
      this.activeTab = 'enhancements';
      setTimeout(() => this.applyEnhancements(), 100);
    }
  }

  /**
   * Save image (alias for save)
   */
  saveImage() {
    this.save();
  }

  // ============ BACKGROUND ============

  /**
   * Remove background using AI service
   */
  async removeBackground() {
    const imageToProcess = this.correctedImage || this.originalImage;
    if (!imageToProcess) {
      alert(this.transloco.translate('imageEditor.pleaseLoadImageFirst'));
      return;
    }

    // Save the current image before removal so it can be restored
    this.imageBeforeBackgroundRemoval = imageToProcess;

    this.isProcessing = true;
    this.processingMessage = this.transloco.translate('imageEditor.removingBackground');

    try {
      // Call the background removal API
      const resultImage = await this.backgroundRemovalService.removeBackgroundFromImage(imageToProcess);

      // Apply alpha erosion to clean up edges (remove white fringe)
      const cleanupCanvas = document.createElement('canvas');
      cleanupCanvas.width = resultImage.width;
      cleanupCanvas.height = resultImage.height;
      const ctx = cleanupCanvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(resultImage, 0, 0);
        this.backgroundRemovalService.applyAlphaErosion(cleanupCanvas, 1);

        // Convert back to image
        const cleanedImage = new Image();
        cleanedImage.onload = () => {
          this.correctedImage = cleanedImage;
          this.backgroundRemoved = true;
          this.isProcessing = false;
          this.processingMessage = '';
          // Auto-switch to background accordion after removal
          this.activeAccordion = 'background';
          this.activeLayer = 'background';
          this.applyEnhancementsWithBackground();
        };
        cleanedImage.onerror = () => {
          this.correctedImage = resultImage;
          this.backgroundRemoved = true;
          this.isProcessing = false;
          this.processingMessage = '';
          // Auto-switch to background accordion after removal
          this.activeAccordion = 'background';
          this.activeLayer = 'background';
          this.applyEnhancementsWithBackground();
        };
        cleanedImage.src = cleanupCanvas.toDataURL('image/png');
      } else {
        // Fallback without cleanup
        this.correctedImage = resultImage;
        this.backgroundRemoved = true;
        this.isProcessing = false;
        this.processingMessage = '';
        // Auto-switch to background accordion after removal
        this.activeAccordion = 'background';
        this.activeLayer = 'background';
        this.applyEnhancementsWithBackground();
      }
    } catch (error: any) {
      console.error('Background removal failed:', error);
      this.isProcessing = false;
      this.processingMessage = '';
      alert(this.transloco.translate('imageEditor.backgroundRemovalFailed') + '\n' + (error?.message || ''));
    }
  }

  /**
   * Set background color
   */
  setBackgroundColor(color: string) {
    this.backgroundColor = color;
    // Apply background color to canvas
    this.applyEnhancements();
  }

  // ============ PERSPECTIVE WARP MODE ============

  /**
   * Toggle perspective warp mode on/off
   */
  togglePerspectiveWarpMode() {
    this.perspectiveWarpMode = !this.perspectiveWarpMode;

    if (this.perspectiveWarpMode) {
      // Initialize warp corners based on current image position
      this.initializeWarpCorners();
      this.warpNeedsUpdate = false;
    } else {
      // Exit warp mode
      this.warpDraggingCorner = -1;
    }

    this.applyEnhancements();
  }

  /**
   * Initialize warp corners to the current image bounding box
   */
  initializeWarpCorners() {
    if (!this.previewCanvas || (!this.correctedImage && !this.originalImage))
      return;

    const canvas = this.previewCanvas.nativeElement;
    const img = this.correctedImage || this.originalImage!;

    // Calculate image bounds on canvas
    const scaleX = this.maxCanvasWidth / img.width;
    const scaleY = this.maxCanvasHeight / img.height;
    const scale = Math.min(scaleX, scaleY, 1);
    const width = img.width * scale;
    const height = img.height * scale;
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;

    // Use perspective service to initialize corners
    this.warpCorners = this.perspectiveService.initializeWarpCorners(
      centerX,
      centerY,
      width,
      height,
      this.rotation,
    );
  }

  /**
   * Reset warp corners to a rectangle (undo warp distortion)
   */
  resetWarpCorners() {
    this.initializeWarpCorners();
    this.warpNeedsUpdate = false;
    this.applyEnhancements();
  }

  /**
   * Apply perspective warp using OpenCV (high-quality final render)
   */
  async applyPerspectiveWarp() {
    if (this.warpCorners.length !== 4 || !this.originalImage) return;

    if (!this.openCVLoaded) {
      this.processingMessage = this.transloco.translate('imageEditor.loadingOpenCV');
      this.isProcessing = true;
      await this.loadOpenCV();
      if (!this.openCVLoaded) {
        this.isProcessing = false;
        alert(this.transloco.translate('imageEditor.failedToLoadOpenCV'));
        return;
      }
    }

    const img = this.correctedImage || this.originalImage;
    const canvas = this.previewCanvas?.nativeElement;
    if (!canvas) return;

    this.isProcessing = true;
    this.processingMessage = this.transloco.translate('imageEditor.applyingPerspectiveWarp');

    try {
      // Calculate scale between canvas and original image
      const scaleX = this.maxCanvasWidth / img.width;
      const scaleY = this.maxCanvasHeight / img.height;
      const canvasScale = Math.min(scaleX, scaleY, 1);

      // Scale corners from canvas coordinates to image coordinates
      const scaledCorners = this.warpCorners.map((c) => ({
        x: c.x / canvasScale,
        y: c.y / canvasScale,
      }));

      // Use perspective service for the warp transformation
      const result = await this.perspectiveService.applyPerspectiveWarp(
        img,
        scaledCorners,
      );

      // Replace the current image with the warped version
      this.correctedImage = result.image;

      // Reset transforms
      this.rotation = 0;
      this.flipH = false;
      this.flipV = false;

      // Exit warp mode
      this.perspectiveWarpMode = false;
      this.warpCorners = [];
      this.warpNeedsUpdate = false;

      this.isProcessing = false;
      this.applyEnhancements();
    } catch (error) {
      console.error('Perspective warp failed:', error);
      this.isProcessing = false;
      alert(this.transloco.translate('imageEditor.failedToApplyPerspectiveWarp'));
    }
  }

  // ============ COMPOSE (CROP, ROTATE, FLIP) ============

  /**
   * Handle mouse down on preview canvas for transform operations
   */
  onPreviewMouseDown(event: MouseEvent) {
    if (!this.previewCanvas) return;
    if (this.perspectiveCorrectionMode) return; // Block during perspective corner selection only

    const canvas = this.previewCanvas.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const mouseX = (event.clientX - rect.left) * scaleX;
    const mouseY = (event.clientY - rect.top) * scaleY;

    // Handle perspective warp mode - check for warp corner handles or pan
    if (this.perspectiveWarpMode && this.warpCorners.length === 4) {
      // First check if clicking on a corner handle
      const cornerIndex = this.perspectiveService.findCornerAtPosition(
        this.warpCorners,
        mouseX,
        mouseY,
        15,
      );
      if (cornerIndex >= 0) {
        event.preventDefault();
        this.warpDraggingCorner = cornerIndex;
        this.isDragging = true;
        this.dragType = 'warp-corner';
        return;
      }

      // If not on a corner, check if inside the warp quadrilateral for pan operation
      if (
        this.perspectiveService.isPointInsideQuad(
          this.warpCorners,
          mouseX,
          mouseY,
        )
      ) {
        event.preventDefault();
        this.isDragging = true;
        this.dragType = 'warp-pan';
        this.warpPanStartX = mouseX;
        this.warpPanStartY = mouseY;
        this.warpPanStartCorners = this.warpCorners.map((c) => ({
          x: c.x,
          y: c.y,
        }));
        return;
      }
    }

    // Regular transform handles (when not in warp mode) - CLICK TO SELECT LAYER
    if (!this.perspectiveWarpMode && !this.perspectiveCorrectionMode) {
      // Canvas sizes matching prototype exactly
      const outputCanvasSize = 1200;
      const exportOffset = 200;
      const marginFactor = 0.65;
      const availableSize = outputCanvasSize * marginFactor;

      const img = this.correctedImage || this.originalImage;
      if (!img) return;

      // Calculate subject image bounds
      const imgAspect = img.width / img.height;
      let baseImageWidth: number;
      let baseImageHeight: number;
      if (imgAspect > 1) {
        baseImageWidth = availableSize;
        baseImageHeight = availableSize / imgAspect;
      } else {
        baseImageHeight = availableSize;
        baseImageWidth = availableSize * imgAspect;
      }
      const scaledWidth = baseImageWidth * (this.imageScaleX / 100);
      const scaledHeight = baseImageHeight * (this.imageScaleY / 100);
      const subjectPosX = exportOffset + (this.imagePositionX / 100) * outputCanvasSize;
      const subjectPosY = exportOffset + (this.imagePositionY / 100) * outputCanvasSize;

      // Calculate background image bounds (if exists)
      let bgBounds: { x: number; y: number; width: number; height: number } | null = null;
      if (this.backgroundImage && this.backgroundType === 'image') {
        const bgScale = this.backgroundImageScale / 100;
        const bgWidth = this.backgroundImage.width * bgScale;
        const bgHeight = this.backgroundImage.height * bgScale;
        const bgX = exportOffset + (this.backgroundImagePositionX / 100) * outputCanvasSize - bgWidth / 2;
        const bgY = exportOffset + (this.backgroundImagePositionY / 100) * outputCanvasSize - bgHeight / 2;
        bgBounds = { x: bgX, y: bgY, width: bgWidth, height: bgHeight };
      }

      // Check if clicking on BACKGROUND handles/image first (background is behind subject)
      if (this.activeLayer === 'background' && bgBounds) {
        // Check background handles
        const bgHandle = this.perspectiveService.findTransformHandle(
          mouseX, mouseY,
          bgBounds.x, bgBounds.y,
          bgBounds.width, bgBounds.height,
          0, // No rotation for background
          20
        );
        if (bgHandle) {
          event.preventDefault();
          this.isDragging = true;
          this.dragType = 'bg-' + bgHandle;
          this.bgDragStartX = mouseX;
          this.bgDragStartY = mouseY;
          this.initialBgPositionX = this.backgroundImagePositionX;
          this.initialBgPositionY = this.backgroundImagePositionY;
          this.initialBgScale = this.backgroundImageScale;
          this.currentCursor = bgHandle === 'move' ? 'move' : 'nwse-resize';
          return;
        }
      }

      // Check if clicking on SHADOW layer (when shadow is active and enabled)
      if (this.activeLayer === 'shadow' && this.shadowLayerEnabled && this.shadowImage) {
        // Calculate shadow bounds - shadow is positioned relative to subject center
        const shadowScale = this.shadowLayerScale / 100;
        const shadowWidth = scaledWidth * shadowScale;
        const shadowHeight = (this.shadowImage.height / this.shadowImage.width) * shadowWidth;
        const shadowX = subjectPosX + this.shadowLayerOffsetX;
        const shadowY = subjectPosY + this.shadowLayerOffsetY;

        // Check if click is within shadow bounds (rectangular hit test, ignoring rotation for simplicity)
        const halfW = shadowWidth / 2;
        const halfH = shadowHeight / 2;
        if (mouseX >= shadowX - halfW && mouseX <= shadowX + halfW &&
            mouseY >= shadowY - halfH && mouseY <= shadowY + halfH) {
          event.preventDefault();
          this.isDragging = true;
          this.dragType = 'shadow-move';
          this.shadowDragStartX = mouseX;
          this.shadowDragStartY = mouseY;
          this.initialShadowOffsetX = this.shadowLayerOffsetX;
          this.initialShadowOffsetY = this.shadowLayerOffsetY;
          this.currentCursor = 'move';
          return;
        }
      }

      // Check if clicking on SUBJECT handles (when subject is active)
      if (this.activeLayer === 'subject') {
        const handle = this.perspectiveService.findTransformHandle(
          mouseX, mouseY,
          subjectPosX - scaledWidth / 2,
          subjectPosY - scaledHeight / 2,
          scaledWidth, scaledHeight,
          this.rotation,
          20
        );

        if (handle) {
          event.preventDefault();
          this.isDragging = true;
          this.dragType = handle;
          this.showTransformHandles = true;
          this.dragStartX = event.clientX;
          this.dragStartY = event.clientY;
          this.initialRotation = this.rotation;
          this.initialScale = this.imageScale;
          this.initialOffsetX = this.imageOffsetX;
          this.initialOffsetY = this.imageOffsetY;
          this.initialScaleX = this.imageScaleX;
          this.initialScaleY = this.imageScaleY;
          this.initialPositionX = this.imagePositionX;
          this.initialPositionY = this.imagePositionY;
          return;
        }
      }

      // CLICK TO SELECT: Check if clicking inside background image -> select background
      if (bgBounds && mouseX >= bgBounds.x && mouseX <= bgBounds.x + bgBounds.width &&
          mouseY >= bgBounds.y && mouseY <= bgBounds.y + bgBounds.height) {
        // Check if also inside subject (subject is on top)
        const inSubject = this.isPointInsideSubject(mouseX, mouseY, subjectPosX, subjectPosY, scaledWidth, scaledHeight);
        if (!inSubject) {
          // Clicked on background, not subject - select background
          this.activeLayer = 'background';
          this.showTransformHandles = true;
          this.applyEnhancementsWithBackground();
          return;
        }
      }

      // CLICK TO SELECT: Check if clicking inside subject image -> select subject
      if (this.isPointInsideSubject(mouseX, mouseY, subjectPosX, subjectPosY, scaledWidth, scaledHeight)) {
        this.activeLayer = 'subject';
        this.showTransformHandles = true;
        this.applyEnhancementsWithBackground();
        return;
      }

      // Clicked outside both - deselect
      this.showTransformHandles = false;
      this.applyEnhancementsWithBackground();
    }
  }

  /**
   * Check if a point is inside the subject image (accounting for rotation)
   */
  isPointInsideSubject(mouseX: number, mouseY: number, centerX: number, centerY: number, width: number, height: number): boolean {
    // Transform mouse to subject's local coordinates (accounting for rotation)
    const rotRad = (this.rotation * Math.PI) / 180;
    const dx = mouseX - centerX;
    const dy = mouseY - centerY;
    const localX = dx * Math.cos(-rotRad) - dy * Math.sin(-rotRad);
    const localY = dx * Math.sin(-rotRad) + dy * Math.cos(-rotRad);
    return Math.abs(localX) <= width / 2 && Math.abs(localY) <= height / 2;
  }

  /**
   * Handle mouse move on preview canvas
   */
  onPreviewMouseMove(event: MouseEvent) {
    if (!this.previewCanvas) return;

    const canvas = this.previewCanvas.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const mouseX = (event.clientX - rect.left) * scaleX;
    const mouseY = (event.clientY - rect.top) * scaleY;

    // Fixed canvas constants (matching applyEnhancementsWithBackground)
    const outputCanvasSize = 1200;
      const exportOffset = 200;
    const marginFactor = 0.65;

    // Update cursor based on what's under the mouse
    if (!this.isDragging) {
      // Background layer selected - show move cursor over background
      if (this.activeLayer === 'background' && this.backgroundImage && this.backgroundType === 'image') {
        this.currentCursor = 'move';
      }

      // Shadow layer selected - show move cursor over shadow area
      if (this.activeLayer === 'shadow' && this.shadowLayerEnabled && this.shadowImage) {
        this.currentCursor = 'move';
      }

      if (this.perspectiveWarpMode && this.warpCorners.length === 4) {
        // Check if over a warp corner
        const cornerIndex = this.perspectiveService.findCornerAtPosition(
          this.warpCorners,
          mouseX,
          mouseY,
          15,
        );
        if (cornerIndex >= 0) {
          this.currentCursor = 'grab';
        } else if (
          this.perspectiveService.isPointInsideQuad(
            this.warpCorners,
            mouseX,
            mouseY,
          )
        ) {
          this.currentCursor = 'move';
        } else {
          this.currentCursor = 'default';
        }
      } else if (!this.perspectiveCorrectionMode) {
        // Calculate actual image bounds for cursor detection
        const img = this.correctedImage || this.originalImage;
        if (img) {
          const availableSize = outputCanvasSize * marginFactor;
          const imgAspect = img.width / img.height;

          let baseImageWidth: number;
          let baseImageHeight: number;
          if (imgAspect > 1) {
            baseImageWidth = availableSize;
            baseImageHeight = availableSize / imgAspect;
          } else {
            baseImageHeight = availableSize;
            baseImageWidth = availableSize * imgAspect;
          }

          const scaledWidth = baseImageWidth * (this.imageScaleX / 100);
          const scaledHeight = baseImageHeight * (this.imageScaleY / 100);
          // Position with export offset
          const posX = exportOffset + (this.imagePositionX / 100) * outputCanvasSize;
          const posY = exportOffset + (this.imagePositionY / 100) * outputCanvasSize;

          const handle = this.perspectiveService.findTransformHandle(
            mouseX,
            mouseY,
            posX - scaledWidth / 2,
            posY - scaledHeight / 2,
            scaledWidth,
            scaledHeight,
            this.rotation,
            20,
          );

          switch (handle) {
            case 'tl':
            case 'br':
              this.currentCursor = 'nwse-resize';
              break;
            case 'tr':
            case 'bl':
              this.currentCursor = 'nesw-resize';
              break;
            case 'tc':
            case 'bc':
              this.currentCursor = 'ns-resize';
              break;
            case 'ml':
            case 'mr':
              this.currentCursor = 'ew-resize';
              break;
            case 'rotate':
              this.currentCursor = 'grab';
              break;
            case 'move':
              this.currentCursor = 'move';
              break;
            default:
              this.currentCursor = 'default';
          }
        }
      }
    }

    // Handle dragging
    if (this.isDragging) {
      // Handle warp corner dragging
      if (this.dragType === 'warp-corner' && this.warpDraggingCorner >= 0) {
        // Update the corner position directly to mouse position
        this.warpCorners[this.warpDraggingCorner] = { x: mouseX, y: mouseY };
        this.warpNeedsUpdate = true;

        // Use requestAnimationFrame for smooth visual updates
        requestAnimationFrame(() => {
          if (this.isDragging && this.dragType === 'warp-corner') {
            this.applyEnhancements();
          }
        });
        return;
      }

      // Handle warp pan (move all corners together)
      if (this.dragType === 'warp-pan' && this.warpPanStartCorners.length === 4) {
        const deltaX = mouseX - this.warpPanStartX;
        const deltaY = mouseY - this.warpPanStartY;

        // Move all corners by the same delta
        this.warpCorners = this.warpPanStartCorners.map((c) => ({
          x: c.x + deltaX,
          y: c.y + deltaY,
        }));
        this.warpNeedsUpdate = true;

        // Use requestAnimationFrame for smooth visual updates
        requestAnimationFrame(() => {
          if (this.isDragging && this.dragType === 'warp-pan') {
            this.applyEnhancements();
          }
        });
        return;
      }

      // Handle background image dragging (move)
      if (this.dragType === 'bg-move' && this.backgroundImage) {
        const deltaX = mouseX - this.bgDragStartX;
        const deltaY = mouseY - this.bgDragStartY;

        // Convert pixel delta to percentage position change
        const positionScale = 100 / outputCanvasSize;
        this.backgroundImagePositionX = this.initialBgPositionX + (deltaX * positionScale);
        this.backgroundImagePositionY = this.initialBgPositionY + (deltaY * positionScale);

        // Clamp to 0-100 range
        this.backgroundImagePositionX = Math.max(0, Math.min(100, this.backgroundImagePositionX));
        this.backgroundImagePositionY = Math.max(0, Math.min(100, this.backgroundImagePositionY));

        requestAnimationFrame(() => {
          if (this.isDragging && this.dragType === 'bg-move') {
            this.applyEnhancementsWithBackground();
          }
        });
        return;
      }

      // Handle background image resize (corner handles)
      if (this.dragType.startsWith('bg-') && this.dragType !== 'bg-move' && this.backgroundImage) {
        const deltaX = mouseX - this.bgDragStartX;
        const deltaY = mouseY - this.bgDragStartY;

        // Corner resize - proportional scaling
        if (['bg-tl', 'bg-tr', 'bg-bl', 'bg-br'].includes(this.dragType)) {
          const scaleChange = (deltaX + deltaY) / 5;
          this.backgroundImageScale = Math.max(10, Math.min(300, this.initialBgScale + scaleChange));
        }
        // Side resize - could add horizontal/vertical only scaling here

        requestAnimationFrame(() => {
          if (this.isDragging && this.dragType.startsWith('bg-')) {
            this.applyEnhancementsWithBackground();
          }
        });
        return;
      }

      // Handle shadow layer dragging (move)
      if (this.dragType === 'shadow-move') {
        const deltaX = mouseX - this.shadowDragStartX;
        const deltaY = mouseY - this.shadowDragStartY;

        // Update shadow offset directly in pixels (range is ±800)
        this.shadowLayerOffsetX = Math.max(-800, Math.min(800, this.initialShadowOffsetX + deltaX));
        this.shadowLayerOffsetY = Math.max(-800, Math.min(800, this.initialShadowOffsetY + deltaY));

        requestAnimationFrame(() => {
          if (this.isDragging && this.dragType === 'shadow-move') {
            this.applyEnhancementsWithBackground();
          }
        });
        return;
      }

      const deltaX = event.clientX - this.dragStartX;
      const deltaY = event.clientY - this.dragStartY;

      if (this.dragType === 'move') {
        // Move image position - scale delta to percentage
        const moveScale = 100 / outputCanvasSize;
        this.imagePositionX = this.initialPositionX + (deltaX * moveScale * scaleX);
        this.imagePositionY = this.initialPositionY + (deltaY * moveScale * scaleY);

        // Clamp position to reasonable bounds (0-100)
        this.imagePositionX = Math.max(0, Math.min(100, this.imagePositionX));
        this.imagePositionY = Math.max(0, Math.min(100, this.imagePositionY));

        this.currentCursor = 'move';

        requestAnimationFrame(() => {
          if (this.isDragging && this.dragType === 'move') {
            this.applyEnhancementsWithBackground();
          }
        });
      } else if (this.dragType === 'rotate') {
        // Calculate rotation based on mouse movement around image center
        // Image center in canvas coordinates (with export offset)
        const posX = exportOffset + (this.imagePositionX / 100) * outputCanvasSize;
        const posY = exportOffset + (this.imagePositionY / 100) * outputCanvasSize;
        const centerScreenX = rect.left + (posX / canvas.width) * rect.width;
        const centerScreenY = rect.top + (posY / canvas.height) * rect.height;

        const startAngle = Math.atan2(
          this.dragStartY - centerScreenY,
          this.dragStartX - centerScreenX,
        );
        const currentAngle = Math.atan2(
          event.clientY - centerScreenY,
          event.clientX - centerScreenX,
        );
        const angleDiff = ((currentAngle - startAngle) * 180) / Math.PI;
        let newRotation = Math.round(this.initialRotation + angleDiff);

        // Apply rotation snapping (0°, 45°, 90°, etc.)
        const snapAngles = [0, 45, 90, 135, 180, -45, -90, -135, -180];
        const snapThreshold = 5;
        for (const snapAngle of snapAngles) {
          if (Math.abs(newRotation - snapAngle) < snapThreshold) {
            newRotation = snapAngle;
            break;
          }
        }

        // Clamp rotation to -180 to 180
        while (newRotation > 180) newRotation -= 360;
        while (newRotation < -180) newRotation += 360;

        this.rotation = newRotation;
        this.currentCursor = 'grabbing';

        requestAnimationFrame(() => {
          if (this.isDragging && this.dragType === 'rotate') {
            this.applyEnhancementsWithBackground();
          }
        });
      } else if (['tl', 'tr', 'bl', 'br'].includes(this.dragType)) {
        // Corner handles - proportional scaling
        const scaleFactor = 1 + (deltaX + deltaY) * 0.005;
        this.imageScaleX = Math.max(10, Math.min(300, this.initialScaleX * scaleFactor));
        this.imageScaleY = Math.max(10, Math.min(300, this.initialScaleY * scaleFactor));

        requestAnimationFrame(() => {
          if (this.isDragging) {
            this.applyEnhancementsWithBackground();
          }
        });
      } else if (['tc', 'bc'].includes(this.dragType)) {
        // Top/bottom handles - vertical scaling
        const scaleFactor = 1 + deltaY * 0.01 * (this.dragType === 'tc' ? -1 : 1);
        this.imageScaleY = Math.max(10, Math.min(300, this.initialScaleY * scaleFactor));

        requestAnimationFrame(() => {
          if (this.isDragging) {
            this.applyEnhancementsWithBackground();
          }
        });
      } else if (['ml', 'mr'].includes(this.dragType)) {
        // Left/right handles - horizontal scaling
        const scaleFactor = 1 + deltaX * 0.01 * (this.dragType === 'ml' ? -1 : 1);
        this.imageScaleX = Math.max(10, Math.min(300, this.initialScaleX * scaleFactor));

        requestAnimationFrame(() => {
          if (this.isDragging) {
            this.applyEnhancementsWithBackground();
          }
        });
      }
    }
  }

  /**
   * Handle mouse up to stop dragging
   */
  onPreviewMouseUp() {
    const wasDragging = this.isDragging;
    this.isDragging = false;
    this.dragType = '';
    this.warpDraggingCorner = -1;

    // Final render after any drag ends - keep handles visible!
    if (wasDragging) {
      // Keep transform handles visible after drag (like prototype)
      this.showTransformHandles = true;
      this.applyEnhancementsWithBackground();
    }
  }

  /**
   * Handle mouse leave
   */
  onPreviewMouseLeave() {
    this.isDragging = false;
    this.dragType = '';
    this.warpDraggingCorner = -1;
    this.currentCursor = 'default';
  }

  /**
   * Apply rotation to image
   */
  applyRotation() {
    // Use throttled render for slider changes
    this.scheduleRender();
  }

  /**
   * Rotate left by 90 degrees
   */
  rotateLeft() {
    this.rotation = (this.rotation - 90) % 360;
    this.applyRotation();
  }

  /**
   * Rotate right by 90 degrees
   */
  rotateRight() {
    this.rotation = (this.rotation + 90) % 360;
    this.applyRotation();
  }

  /**
   * Flip horizontally
   */
  flipHorizontal() {
    this.flipH = !this.flipH;
    this.applyRotation();
  }

  /**
   * Flip vertically
   */
  flipVertical() {
    this.flipV = !this.flipV;
    this.applyRotation();
  }

  /**
   * Apply the current transformations (rotation, flip) permanently
   */
  applyCrop() {
    if (!this.previewCanvas || !this.originalImage) return;

    const img = this.correctedImage || this.originalImage;

    // Create a canvas with the rotated/flipped image
    const canvas = document.createElement('canvas');

    // For 90/270 degree rotations, swap width and height
    const isRotated90 = Math.abs(this.rotation) === 90 || Math.abs(this.rotation) === 270;
    canvas.width = isRotated90 ? img.height : img.width;
    canvas.height = isRotated90 ? img.width : img.height;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    // Apply transforms
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((this.rotation * Math.PI) / 180);
    ctx.scale(this.flipH ? -1 : 1, this.flipV ? -1 : 1);
    ctx.translate(-img.width / 2, -img.height / 2);
    ctx.drawImage(img, 0, 0);

    // Create new image from canvas
    const newImg = new Image();
    newImg.src = canvas.toDataURL('image/png');
    newImg.onload = () => {
      this.correctedImage = newImg;
      // Reset transforms since they're now baked into the image
      this.rotation = 0;
      this.flipH = false;
      this.flipV = false;
      this.applyEnhancements();
    };
  }

  /**
   * Reset crop and rotation
   */
  resetCrop() {
    this.rotation = 0;
    this.flipH = false;
    this.flipV = false;
    this.applyEnhancements();
  }

  /**
   * Check if any enhancements are applied
   */
  hasEnhancements(): boolean {
    const defaults = this.enhancementService.getDefaultEnhancements();
    return (
      this.enhancements.brightness !== defaults.brightness ||
      this.enhancements.contrast !== defaults.contrast ||
      this.enhancements.saturation !== defaults.saturation ||
      this.enhancements.temperature !== defaults.temperature ||
      this.enhancements.tint !== defaults.tint ||
      this.enhancements.vibrance !== defaults.vibrance ||
      this.enhancements.highlights !== defaults.highlights ||
      this.enhancements.shadows !== defaults.shadows
    );
  }

  // ============ GRADIENT BACKGROUND ============

  /**
   * Apply a composition preset
   */
  applyPreset(preset: CompositionPreset, index: number) {
    this.activePresetIndex = index;
    this.backgroundColor = preset.backgroundColor;
    this.backgroundType = preset.backgroundType;

    if (preset.backgroundType === 'gradient' && preset.gradientColor1 && preset.gradientColor2) {
      this.gradientColor1 = preset.gradientColor1;
      this.gradientColor2 = preset.gradientColor2;
      this.gradientAngle = preset.gradientAngle || 180;
    }

    this.applyEnhancementsWithBackground();
  }

  /**
   * Apply enhancements with background
   * Uses a fixed square canvas (like the portable edition) so background is always visible when rotating
   */
  applyEnhancementsWithBackground() {
    if (!this.previewCanvas || (!this.correctedImage && !this.originalImage)) return;

    const canvas = this.previewCanvas.nativeElement;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    const img = this.correctedImage || this.originalImage!;

    // Canvas sizes - MUST match prototype exactly (1200/1600)
    const outputCanvasSize = 1200; // Export area size (matches prototype)
    const handleMargin = 200; // Margin on each side for handles to extend beyond
    const workingCanvasSize = outputCanvasSize + (handleMargin * 2); // 1600px total

    // Set canvas size - includes margin for handles
    canvas.width = workingCanvasSize;
    canvas.height = workingCanvasSize;

    // Calculate export area offset (centered within working canvas)
    const exportOffsetX = handleMargin;
    const exportOffsetY = handleMargin;

    // Calculate scale to fit image within the export area (with margin for rotation)
    const marginFactor = 0.65; // Use 65% of export area for image, leaving room for rotation
    const availableSize = outputCanvasSize * marginFactor;
    const imgAspect = img.width / img.height;

    let baseImageWidth: number;
    let baseImageHeight: number;

    if (imgAspect > 1) {
      // Landscape image
      baseImageWidth = availableSize;
      baseImageHeight = availableSize / imgAspect;
    } else {
      // Portrait or square image
      baseImageHeight = availableSize;
      baseImageWidth = availableSize * imgAspect;
    }

    this.imageScale = baseImageWidth / img.width;

    // Draw background on the entire canvas (fills entire area like portable edition)
    const bgConfig: BackgroundConfig = {
      type: this.backgroundType,
      color: this.backgroundColor || '#ffffff',
      gradientColor1: this.gradientColor1,
      gradientColor2: this.gradientColor2,
      gradientAngle: this.gradientAngle,
      image: this.backgroundImage,
      imageScale: this.backgroundImageScale,
      imagePositionX: this.backgroundImagePositionX,
      imagePositionY: this.backgroundImagePositionY,
      imageOpacity: this.backgroundImageOpacity,
      imageBlur: this.backgroundImageBlur,
      imageBrightness: this.backgroundImageBrightness,
      imageContrast: this.backgroundImageContrast,
      imageSaturation: this.backgroundImageSaturation,
      imageTemperature: this.backgroundImageTemperature,
      imageTint: this.backgroundImageTint,
      imageHighlights: this.backgroundImageHighlights,
      imageShadows: this.backgroundImageShadows,
      imageMidtones: this.backgroundImageMidtones
    };
    this.canvasCompositorService.drawBackground(ctx, workingCanvasSize, workingCanvasSize, bgConfig);

    // Draw shadow layer if enabled
    if (this.shadowLayerEnabled && this.shadowImage) {
      const shadowConfig = {
        enabled: true,
        image: this.shadowImage,
        scale: this.shadowLayerScale,
        offsetX: this.shadowLayerOffsetX,
        offsetY: this.shadowLayerOffsetY,
        opacity: this.shadowLayerOpacity,
        rotation: this.shadowLayerRotation,
        blur: this.shadowLayerBlur
      };
      this.canvasCompositorService.drawShadowLayer(
        ctx,
        shadowConfig,
        exportOffsetX + outputCanvasSize / 2,
        exportOffsetY + outputCanvasSize / 2,
        baseImageWidth * 0.8
      );
    }

    // Apply CSS filters and draw the image
    ctx.filter = this.enhancementService.buildFilterString(this.enhancements);

    // Calculate image dimensions with user scale adjustments (X/Y)
    const imgScaleX = this.imageScaleX / 100;
    const imgScaleY = this.imageScaleY / 100;
    const scaledWidth = baseImageWidth * imgScaleX;
    const scaledHeight = baseImageHeight * imgScaleY;

    // Calculate position based on percentage (0-100) within the export area
    const posX = exportOffsetX + (this.imagePositionX / 100) * outputCanvasSize;
    const posY = exportOffsetY + (this.imagePositionY / 100) * outputCanvasSize;

    // Draw subject layer only if visible
    if (this.subjectLayerVisible) {
      ctx.save();
      // Move to position
      ctx.translate(posX, posY);
      // Apply rotation
      ctx.rotate((this.rotation * Math.PI) / 180);
      // Apply flip
      ctx.scale(this.flipH ? -1 : 1, this.flipV ? -1 : 1);
      // Draw image centered at position with scale
      ctx.drawImage(img, -scaledWidth / 2, -scaledHeight / 2, scaledWidth, scaledHeight);
      ctx.restore();

      ctx.filter = 'none';
      // Apply pixel adjustments to the entire working canvas
      this.enhancementService.applyPixelAdjustments(ctx, workingCanvasSize, workingCanvasSize, this.enhancements);
    } else {
      ctx.filter = 'none';
    }

    // Draw canvas guides if enabled
    const guideConfig: CanvasGuideConfig = {
      showGrid: this.showGuideGrid,
      showCenterLines: this.showCenterLines,
      showRuleOfThirds: this.showRuleOfThirds
    };
    this.canvasCompositorService.drawCanvasGuides(ctx, workingCanvasSize, workingCanvasSize, guideConfig);

    // Draw warp corners or transform handles
    if (this.perspectiveWarpMode && this.warpCorners.length === 4) {
      this.perspectiveService.drawWarpCorners(ctx, this.warpCorners, this.warpDraggingCorner, 20);
    } else if (this.showTransformHandles && !this.perspectiveCorrectionMode) {
      if (this.activeLayer === 'subject' && this.subjectLayerVisible) {
        // Draw handles around the subject image (only if visible)
        this.perspectiveService.drawTransformHandles(
          ctx,
          posX - scaledWidth / 2,
          posY - scaledHeight / 2,
          scaledWidth,
          scaledHeight,
          this.rotation,
          20
        );
      } else if (this.activeLayer === 'background' && this.backgroundImage && this.backgroundType === 'image') {
        // Draw handles around the background image
        const bgScale = this.backgroundImageScale / 100;
        const bgWidth = this.backgroundImage.width * bgScale;
        const bgHeight = this.backgroundImage.height * bgScale;
        const bgX = exportOffsetX + (this.backgroundImagePositionX / 100) * outputCanvasSize - bgWidth / 2;
        const bgY = exportOffsetY + (this.backgroundImagePositionY / 100) * outputCanvasSize - bgHeight / 2;
        this.perspectiveService.drawTransformHandles(
          ctx,
          bgX,
          bgY,
          bgWidth,
          bgHeight,
          0, // No rotation for background
          20
        );
      }
    }

    // Draw export area boundary (cyan dashed line like portable edition)
    this.drawExportAreaBoundary(ctx, outputCanvasSize, exportOffsetX, exportOffsetY);
  }

  /**
   * Draw the export area boundary like portable edition (cyan dashed line around export area)
   */
  drawExportAreaBoundary(ctx: CanvasRenderingContext2D, exportSize: number, offsetX: number, offsetY: number) {
    ctx.save();

    // Draw dashed cyan border around the export area (like "1200×1200 Export Area")
    ctx.strokeStyle = '#00bcd4'; // Cyan color like portable edition
    ctx.lineWidth = 2;
    ctx.setLineDash([8, 4]);
    ctx.strokeRect(offsetX, offsetY, exportSize, exportSize);

    // Draw label at top-left of export area
    ctx.setLineDash([]);
    ctx.fillStyle = '#00bcd4';
    ctx.font = 'bold 10px Arial';
    ctx.fillText('1200×1200 Export Area', offsetX + 6, offsetY + 14);

    ctx.restore();
  }

  // ============ SHADOW LAYER ============

  /**
   * Load a shadow preset
   */
  loadShadowPreset(presetId: string) {
    this.shadowLayerPreset = presetId;
    const img = this.correctedImage || this.originalImage;
    if (!img) return;

    const shadowCanvas = this.shadowEffectsService.generateProgrammaticShadow(presetId, img);
    if (shadowCanvas) {
      const shadowImg = new Image();
      shadowImg.onload = () => {
        this.shadowImage = shadowImg;
        this.applyEnhancementsWithBackground();
      };
      shadowImg.src = shadowCanvas.toDataURL();
    } else {
      this.shadowImage = null;
      this.applyEnhancementsWithBackground();
    }
  }

  /**
   * Toggle shadow layer on/off
   */
  toggleShadowLayer() {
    if (this.shadowLayerEnabled && this.shadowLayerPreset !== 'none') {
      this.loadShadowPreset(this.shadowLayerPreset);
    } else {
      this.shadowImage = null;
      this.applyEnhancementsWithBackground();
    }
  }

  /**
   * Reset shadow layer settings
   */
  resetShadowLayer() {
    this.shadowLayerEnabled = false;
    this.shadowLayerPreset = 'none';
    this.shadowLayerScale = 100;
    this.shadowLayerOffsetX = 0;
    this.shadowLayerOffsetY = 20;
    this.shadowLayerOpacity = 50;
    this.shadowLayerRotation = 0;
    this.shadowLayerBlur = 15;
    this.shadowLayerShape = 'ellipse';
    this.shadowImage = null;
    this.applyEnhancementsWithBackground();
  }

  // ============ PIXABAY INTEGRATION ============

  /**
   * Toggle Pixabay modal visibility
   */
  togglePixabayPanel() {
    this.showPixabayModal = !this.showPixabayModal;
    console.warn('🔍 Pixabay modal toggled:', this.showPixabayModal);
    if (this.showPixabayModal && this.pixabaySearchResults.length === 0) {
      console.warn('🔍 About to search Pixabay category: Studio');
      this.searchPixabayByCategory('Studio');
    }
  }

  /**
   * Search Pixabay images
   */
  searchPixabay() {
    if (!this.pixabaySearchQuery.trim()) return;

    this.pixabayLoading = true;
    this.pixabaySelectedCategory = '';
    this.pixabayCurrentPage = 1;

    this.pixabayService.searchImages(this.pixabaySearchQuery, 1).subscribe({
      next: (response: PixabayResponse) => {
        this.pixabaySearchResults = response.hits;
        this.pixabayTotalHits = response.totalHits;
        this.pixabayHasMore = response.hits.length < response.totalHits;
        this.pixabayLoading = false;
      },
      error: () => {
        this.pixabayLoading = false;
        this.pixabaySearchResults = [];
        this.pixabayHasMore = false;
      }
    });
  }

  /**
   * Search Pixabay by category
   */
  searchPixabayByCategory(categoryName: string) {
    this.pixabayLoading = true;
    this.pixabaySelectedCategory = categoryName;
    this.pixabayCurrentPage = 1;

    this.pixabayService.searchByCategory(categoryName, 1).subscribe({
      next: (response: PixabayResponse) => {
        this.pixabaySearchResults = response.hits;
        this.pixabayTotalHits = response.totalHits;
        this.pixabayHasMore = response.hits.length < response.totalHits;
        this.pixabayLoading = false;
      },
      error: (err) => {
        console.error('Pixabay search error:', err);
        this.pixabayLoading = false;
        this.pixabaySearchResults = [];
        this.pixabayHasMore = false;
      }
    });
  }

  /**
   * Load more Pixabay results
   */
  loadMorePixabay() {
    if (this.pixabayLoading || !this.pixabayHasMore) return;

    this.pixabayLoading = true;
    this.pixabayCurrentPage++;

    const searchObservable = this.pixabaySelectedCategory
      ? this.pixabayService.searchByCategory(this.pixabaySelectedCategory, this.pixabayCurrentPage)
      : this.pixabayService.searchImages(this.pixabaySearchQuery, this.pixabayCurrentPage);

    searchObservable.subscribe({
      next: (response: PixabayResponse) => {
        this.pixabaySearchResults = [...this.pixabaySearchResults, ...response.hits];
        this.pixabayHasMore = this.pixabaySearchResults.length < response.totalHits;
        this.pixabayLoading = false;
      },
      error: () => {
        this.pixabayLoading = false;
        this.pixabayCurrentPage--; // Revert page on error
      }
    });
  }

  /**
   * Select a Pixabay image as background
   */
  async selectPixabayBackground(image: PixabayImage) {
    try {
      this.backgroundImage = await this.pixabayService.loadImage(image.largeImageURL);
      this.backgroundType = 'image';

      // Auto-scale to fill the frame while maintaining aspect ratio
      if (this.backgroundImage) {
        const scaleToFill = this.calculateScaleToFill(
          this.backgroundImage.width,
          this.backgroundImage.height
        );

        // Set the scale and center the image
        this.backgroundImageScale = scaleToFill;
        this.backgroundImagePositionX = 50; // Center horizontally
        this.backgroundImagePositionY = 50; // Center vertically
      }

      this.showPixabayModal = false;
      this.applyEnhancementsWithBackground();
    } catch (error) {
      console.error('Failed to load Pixabay image:', error);
    }
  }

  /**
   * Clear background image
   */
  clearBackgroundImage() {
    this.backgroundImage = null;
    this.backgroundType = 'solid';
    this.activeLayer = 'subject'; // Switch back to subject when background is cleared
    this.applyEnhancementsWithBackground();
  }

  /**
   * Handle background image property changes (position, scale, opacity, blur, enhancements)
   */
  onBackgroundImageChange() {
    this.scheduleRender();
  }

  /**
   * Select subject layer and open enhance accordion
   */
  selectSubjectLayer() {
    this.activeLayer = 'subject';
    this.activeAccordion = 'enhance';
    this.showTransformHandles = true;
    this.applyEnhancementsWithBackground();
  }

  /**
   * Select background layer and open background accordion
   */
  selectBackgroundLayer() {
    this.activeLayer = 'background';
    this.activeAccordion = 'background';
    this.showTransformHandles = true;
    this.applyEnhancementsWithBackground();
  }

  /**
   * Select shadow layer and open shadow accordion
   */
  selectShadowLayer() {
    this.activeLayer = 'shadow';
    this.activeAccordion = 'shadow';
    if (!this.shadowLayerEnabled) {
      this.shadowLayerEnabled = true;
      this.onShadowToggle();
    }
    this.showTransformHandles = true;
    this.applyEnhancementsWithBackground();
  }

  /**
   * Toggle layer visibility
   */
  toggleLayerVisibility(layer: 'subject' | 'shadow' | 'background', event?: Event) {
    if (event) {
      event.stopPropagation(); // Prevent layer selection when clicking visibility icon
    }

    if (layer === 'subject') {
      this.subjectLayerVisible = !this.subjectLayerVisible;
    } else if (layer === 'shadow') {
      this.shadowLayerEnabled = !this.shadowLayerEnabled;
      this.onShadowToggle();
    }
    // Background always visible (no toggle needed)

    this.applyEnhancementsWithBackground();
  }

  // ============ CANVAS GUIDES ============

  /**
   * Toggle canvas guides and refresh preview
   */
  onGuideChange() {
    this.scheduleRender();
  }

  // ============ PROJECT SAVE/LOAD ============

  /**
   * Save project to file
   */
  saveProject() {
    const composition: CompositionState = {
      rotation: this.rotation,
      flipH: this.flipH,
      flipV: this.flipV,
      imagePositionX: this.imageOffsetX,
      imagePositionY: this.imageOffsetY,
      imageScale: this.imageScale
    };

    const background: BackgroundState = {
      type: this.backgroundType,
      color: this.backgroundColor,
      gradientColor1: this.gradientColor1,
      gradientColor2: this.gradientColor2,
      gradientAngle: this.gradientAngle,
      imageDataUrl: this.backgroundImage ? this.imageToDataUrl(this.backgroundImage) : null,
      imageScale: this.backgroundImageScale,
      imagePositionX: this.backgroundImagePositionX,
      imagePositionY: this.backgroundImagePositionY,
      imageOpacity: this.backgroundImageOpacity,
      imageBlur: this.backgroundImageBlur
    };

    const shadowLayer: ShadowLayerState = {
      enabled: this.shadowLayerEnabled,
      presetId: this.shadowLayerPreset,
      scale: this.shadowLayerScale,
      offsetX: this.shadowLayerOffsetX,
      offsetY: this.shadowLayerOffsetY,
      opacity: this.shadowLayerOpacity,
      rotation: this.shadowLayerRotation,
      blur: this.shadowLayerBlur,
      shape: this.shadowLayerShape
    };

    const project = this.projectService.createProjectData(
      this.originalImage,
      this.correctedImage,
      this.perspectiveCorrected,
      this.backgroundRemoved,
      this.enhancements,
      composition,
      background,
      shadowLayer
    );

    this.projectService.downloadProject(project);
  }

  /**
   * Trigger project file load dialog
   */
  triggerProjectLoad() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,.artflow';
    input.onchange = (e: Event) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        this.loadProjectFile(file);
      }
    };
    input.click();
  }

  /**
   * Load project from file
   */
  async loadProjectFile(file: File) {
    try {
      const project = await this.projectService.loadProjectFromFile(file);

      // Restore original image
      if (project.originalImage) {
        this.originalImage = await this.projectService.loadImageFromDataUrl(project.originalImage);
      }

      // Restore corrected image
      if (project.correctedImage) {
        this.correctedImage = await this.projectService.loadImageFromDataUrl(project.correctedImage);
      }

      // Restore state
      this.perspectiveCorrected = project.perspectiveCorrected;
      this.backgroundRemoved = project.backgroundRemoved;
      this.enhancements = project.enhancements;

      // Restore composition
      this.rotation = project.composition.rotation;
      this.flipH = project.composition.flipH;
      this.flipV = project.composition.flipV;

      // Restore background
      this.backgroundType = project.background.type;
      this.backgroundColor = project.background.color;
      this.gradientColor1 = project.background.gradientColor1;
      this.gradientColor2 = project.background.gradientColor2;
      this.gradientAngle = project.background.gradientAngle;
      this.backgroundImageScale = project.background.imageScale;
      this.backgroundImagePositionX = project.background.imagePositionX;
      this.backgroundImagePositionY = project.background.imagePositionY;
      this.backgroundImageOpacity = project.background.imageOpacity;
      this.backgroundImageBlur = project.background.imageBlur;

      if (project.background.imageDataUrl) {
        this.backgroundImage = await this.projectService.loadImageFromDataUrl(project.background.imageDataUrl);
      }

      // Restore shadow layer
      this.shadowLayerEnabled = project.shadowLayer.enabled;
      this.shadowLayerPreset = project.shadowLayer.presetId;
      this.shadowLayerScale = project.shadowLayer.scale;
      this.shadowLayerOffsetX = project.shadowLayer.offsetX;
      this.shadowLayerOffsetY = project.shadowLayer.offsetY;
      this.shadowLayerOpacity = project.shadowLayer.opacity;
      this.shadowLayerRotation = project.shadowLayer.rotation;
      this.shadowLayerBlur = project.shadowLayer.blur ?? 15;
      this.shadowLayerShape = project.shadowLayer.shape ?? 'ellipse';

      if (this.shadowLayerEnabled) {
        this.generateShadowForShape();
      }

      this.applyEnhancementsWithBackground();
    } catch (error) {
      console.error('Failed to load project:', error);
      alert(this.transloco.translate('imageEditor.failedToLoadProjectFile'));
    }
  }

  /**
   * Convert image to data URL
   */
  private imageToDataUrl(image: HTMLImageElement): string {
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(image, 0, 0);
      return canvas.toDataURL('image/png');
    }
    return '';
  }

  /**
   * Reset all composition settings
   */
  resetCompositionSettings() {
    this.backgroundType = 'solid';
    this.backgroundColor = '#ffffff';
    this.gradientColor1 = '#ffffff';
    this.gradientColor2 = '#e0e0e0';
    this.gradientAngle = 180;
    this.backgroundImage = null;
    this.backgroundImageScale = 100;
    this.backgroundImagePositionX = 50;
    this.backgroundImagePositionY = 50;
    this.backgroundImageOpacity = 100;
    this.backgroundImageBlur = 0;
    this.activePresetIndex = -1;
    this.showGuideGrid = false;
    this.showCenterLines = false;
    this.showRuleOfThirds = false;
    this.resetShadowLayer();
    // Reset scale and position
    this.imageScaleX = 100;
    this.imageScaleY = 100;
    this.scaleLinked = true;
    this.imagePositionX = 50;
    this.imagePositionY = 50;
  }

  // ============ SCALE X/Y CONTROLS ============

  /**
   * Handle Scale X change - update Y if linked
   */
  onScaleXChange() {
    if (this.scaleLinked) {
      this.imageScaleY = this.imageScaleX;
    }
    this.scheduleRender();
  }

  /**
   * Handle Scale Y change - update X if linked
   */
  onScaleYChange() {
    if (this.scaleLinked) {
      this.imageScaleX = this.imageScaleY;
    }
    this.scheduleRender();
  }

  /**
   * Toggle scale linking
   */
  toggleScaleLink() {
    this.scaleLinked = !this.scaleLinked;
    if (this.scaleLinked) {
      // When re-linking, sync Y to X
      this.imageScaleY = this.imageScaleX;
      this.applyEnhancementsWithBackground();
    }
  }

  /**
   * Reset scale to 100%
   */
  resetScale() {
    this.imageScaleX = 100;
    this.imageScaleY = 100;
    this.applyEnhancementsWithBackground();
  }

  // ============ POSITION X/Y CONTROLS ============

  /**
   * Handle Position X change
   */
  onPositionXChange() {
    this.scheduleRender();
  }

  /**
   * Handle Position Y change
   */
  onPositionYChange() {
    this.scheduleRender();
  }

  /**
   * Center the image
   */
  centerImage() {
    this.imagePositionX = 50;
    this.imagePositionY = 50;
    this.applyEnhancementsWithBackground();
  }

  // ============ EXPORT BOUNDARY ============

  /**
   * Toggle export boundary visibility
   */
  toggleExportBoundary() {
    this.showExportBoundary = !this.showExportBoundary;
    this.applyEnhancementsWithBackground();
  }

  /**
   * Draw export area boundary on canvas
   */
  drawExportBoundary(ctx: CanvasRenderingContext2D, canvasWidth: number, canvasHeight: number) {
    if (!this.showExportBoundary) return;

    // Calculate export area bounds (centered)
    const margin = (this.workingCanvasSize - this.exportAreaSize) / 2;
    const scaleToCanvas = Math.min(canvasWidth / this.workingCanvasSize, canvasHeight / this.workingCanvasSize);

    const exportX = margin * scaleToCanvas;
    const exportY = margin * scaleToCanvas;
    const exportW = this.exportAreaSize * scaleToCanvas;
    const exportH = this.exportAreaSize * scaleToCanvas;

    ctx.save();

    // Draw semi-transparent overlay outside export area
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    // Top
    ctx.fillRect(0, 0, canvasWidth, exportY);
    // Bottom
    ctx.fillRect(0, exportY + exportH, canvasWidth, canvasHeight - exportY - exportH);
    // Left
    ctx.fillRect(0, exportY, exportX, exportH);
    // Right
    ctx.fillRect(exportX + exportW, exportY, canvasWidth - exportX - exportW, exportH);

    // Draw export boundary
    ctx.strokeStyle = '#2196F3';
    ctx.lineWidth = 2;
    ctx.setLineDash([8, 4]);
    ctx.strokeRect(exportX, exportY, exportW, exportH);

    // Draw "Export Area" label
    ctx.setLineDash([]);
    ctx.fillStyle = '#2196F3';
    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'left';
    ctx.fillText('Export Area', exportX + 8, exportY + 18);

    ctx.restore();
  }

  // ============ FILE INPUT METHODS ============

  /**
   * Trigger the file input to load a new image
   */
  triggerFileInput() {
    if (this.fileInput) {
      this.fileInput.nativeElement.click();
    }
  }

  /**
   * Trigger the project file input to load a project
   */
  triggerProjectFileInput() {
    if (this.projectFileInput) {
      this.projectFileInput.nativeElement.click();
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string;
        if (dataUrl) {
          this.resetAllState();
          this.loadImage(dataUrl);
        }
      };
      reader.readAsDataURL(file);
    }
    // Reset the input so the same file can be selected again
    input.value = '';
  }

  /**
   * Handle project file input change event
   */
  onProjectFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.loadProjectFile(input.files[0]);
    }
    // Reset the input so the same file can be selected again
    input.value = '';
  }

  // ============ RESTORE METHODS ============

  /**
   * Restore to the original image (undo all edits)
   */
  restoreOriginal() {
    if (!this.originalImage) return;

    this.correctedImage = null;
    this.imageBeforeBackgroundRemoval = null;
    this.backgroundRemoved = false;
    this.perspectiveCorrected = false;
    this.perspectiveCorrectionMode = false;
    this.corners = [];
    this.enhancements = this.enhancementService.getDefaultEnhancements();
    this.rotation = 0;
    this.flipH = false;
    this.flipV = false;
    this.imageScaleX = 100;
    this.imageScaleY = 100;
    this.imagePositionX = 50;
    this.imagePositionY = 50;
    this.resetCompositionSettings();
    this.activeTab = 'enhancements';
    setTimeout(() => this.applyEnhancements(), 100);
  }

  /**
   * Restore the original background (undo background removal)
   */
  restoreOriginalBackground() {
    if (this.imageBeforeBackgroundRemoval) {
      this.correctedImage = this.imageBeforeBackgroundRemoval;
      this.imageBeforeBackgroundRemoval = null;
      this.backgroundRemoved = false;
      this.applyEnhancementsWithBackground();
    } else if (this.originalImage) {
      // If no backup, restore from original
      this.correctedImage = this.originalImage;
      this.backgroundRemoved = false;
      this.applyEnhancementsWithBackground();
    }
  }

  // ============ QUICK PRESETS ============

  /**
   * Apply quick background preset
   */
  applyQuickPreset(preset: string) {
    switch (preset) {
      case 'white':
        this.backgroundType = 'solid';
        this.backgroundColor = '#ffffff';
        break;
      case 'gray':
        this.backgroundType = 'solid';
        this.backgroundColor = '#f0f0f0';
        break;
      case 'gradient':
        this.backgroundType = 'gradient';
        this.gradientColor1 = '#ffffff';
        this.gradientColor2 = '#e0e0e0';
        this.gradientAngle = 180;
        break;
      case 'warm':
        this.backgroundType = 'gradient';
        this.gradientColor1 = '#fff8e1';
        this.gradientColor2 = '#ffe0b2';
        this.gradientAngle = 180;
        break;
      case 'dramatic':
        this.backgroundType = 'gradient';
        this.gradientColor1 = '#424242';
        this.gradientColor2 = '#212121';
        this.gradientAngle = 180;
        break;
    }
    this.applyEnhancementsWithBackground();
  }

  // ============ BACKGROUND TYPE CHANGE ============

  /**
   * Handle background type change
   */
  onBackgroundTypeChange() {
    this.applyEnhancementsWithBackground();
  }

  // ============ PIXABAY BROWSER ============

  /**
   * Open the Pixabay browser panel
   */
  openPixabayBrowser() {
    console.warn('🔍 openPixabayBrowser() called!');
    this.togglePixabayPanel();
  }

  // ============ SHADOW TOGGLE ============

  /**
   * Handle shadow layer toggle change
   */
  onShadowToggle() {
    // Auto-navigate to shadow layer when enabling shadow
    if (this.shadowLayerEnabled) {
      this.activeLayer = 'shadow';
      // Generate shadow based on current shape
      this.generateShadowForShape();
    } else {
      this.shadowImage = null;
      this.applyEnhancementsWithBackground();
    }
  }

  /**
   * Handle shadow layer property changes
   */
  onShadowChange() {
    this.scheduleRender();
  }

  /**
   * Handle shadow shape type change
   */
  onShadowShapeChange() {
    if (this.shadowLayerEnabled) {
      this.generateShadowForShape();
    }
  }

  /**
   * Generate shadow image based on selected shape type
   */
  generateShadowForShape() {
    const img = this.correctedImage || this.originalImage;
    if (!img) return;

    if (this.shadowLayerShape === 'silhouette') {
      // Use existing preset-based shadow generation (follows subject shape)
      if (this.shadowLayerPreset !== 'none') {
        this.loadShadowPreset(this.shadowLayerPreset);
      } else {
        // Default to soft drop for silhouette
        this.shadowLayerPreset = 'drop-soft';
        this.loadShadowPreset('drop-soft');
      }
    } else {
      // Generate geometric shape shadow (ellipse or rectangle)
      this.generateGeometricShadow(img);
    }
  }

  /**
   * Generate an ellipse or rectangle shadow
   */
  generateGeometricShadow(sourceImg: HTMLImageElement) {
    const canvas = document.createElement('canvas');
    const padding = 50;
    canvas.width = sourceImg.width + padding * 2;
    canvas.height = sourceImg.height + padding * 2;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    // Shadow size is based on image width
    const shadowWidth = sourceImg.width * 0.9;
    const shadowHeight = sourceImg.height * 0.2; // Flattened for floor shadow effect

    ctx.save();
    ctx.fillStyle = '#000000';
    ctx.globalAlpha = 1; // Full opacity - we control final opacity in compositor

    if (this.shadowLayerShape === 'ellipse') {
      // Draw ellipse shadow
      ctx.beginPath();
      ctx.ellipse(centerX, centerY + sourceImg.height * 0.4, shadowWidth / 2, shadowHeight / 2, 0, 0, Math.PI * 2);
      ctx.fill();
    } else if (this.shadowLayerShape === 'rectangle') {
      // Draw rounded rectangle shadow
      const rectWidth = shadowWidth;
      const rectHeight = shadowHeight;
      const rectX = centerX - rectWidth / 2;
      const rectY = centerY + sourceImg.height * 0.35;
      const radius = Math.min(rectWidth, rectHeight) * 0.2;

      ctx.beginPath();
      ctx.roundRect(rectX, rectY, rectWidth, rectHeight, radius);
      ctx.fill();
    }

    ctx.restore();

    // Convert to image
    const shadowImg = new Image();
    shadowImg.onload = () => {
      this.shadowImage = shadowImg;
      this.applyEnhancementsWithBackground();
    };
    shadowImg.src = canvas.toDataURL();
  }

  // ============ DOWNLOAD IMAGE ============

  /**
   * Download the enhanced image
   */
  downloadImage() {
    const dataUrl = this.exportImageWithBackground();
    if (!dataUrl) return;

    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = 'artflow-enhanced-image.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Export final image with all backgrounds and effects
   */
  exportImageWithBackground(): string {
    const img = this.correctedImage || this.originalImage;
    if (!img) return '';

    // Create full resolution canvas
    const canvas = document.createElement('canvas');
    canvas.width = this.exportAreaSize;
    canvas.height = this.exportAreaSize;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return '';

    // Draw background
    const bgConfig: BackgroundConfig = {
      type: this.backgroundType,
      color: this.backgroundColor || '#ffffff',
      gradientColor1: this.gradientColor1,
      gradientColor2: this.gradientColor2,
      gradientAngle: this.gradientAngle,
      image: this.backgroundImage,
      imageScale: this.backgroundImageScale,
      imagePositionX: this.backgroundImagePositionX,
      imagePositionY: this.backgroundImagePositionY,
      imageOpacity: this.backgroundImageOpacity,
      imageBlur: this.backgroundImageBlur,
      imageBrightness: this.backgroundImageBrightness,
      imageContrast: this.backgroundImageContrast,
      imageSaturation: this.backgroundImageSaturation,
      imageTemperature: this.backgroundImageTemperature,
      imageTint: this.backgroundImageTint,
      imageHighlights: this.backgroundImageHighlights,
      imageShadows: this.backgroundImageShadows,
      imageMidtones: this.backgroundImageMidtones
    };
    this.canvasCompositorService.drawBackground(ctx, canvas.width, canvas.height, bgConfig);

    // Draw shadow layer if enabled
    if (this.shadowLayerEnabled && this.shadowImage) {
      const shadowConfig = {
        enabled: true,
        image: this.shadowImage,
        scale: this.shadowLayerScale,
        offsetX: this.shadowLayerOffsetX,
        offsetY: this.shadowLayerOffsetY,
        opacity: this.shadowLayerOpacity,
        rotation: this.shadowLayerRotation,
        blur: this.shadowLayerBlur
      };
      this.canvasCompositorService.drawShadowLayer(
        ctx,
        shadowConfig,
        canvas.width / 2,
        canvas.height / 2,
        canvas.width * 0.8
      );
    }

    // Apply CSS filters and draw the image
    ctx.filter = this.enhancementService.buildFilterString(this.enhancements);

    // Calculate image dimensions with scale X/Y
    const imgScaleX = this.imageScaleX / 100;
    const imgScaleY = this.imageScaleY / 100;
    const scaledWidth = canvas.width * imgScaleX;
    const scaledHeight = canvas.height * imgScaleY;

    // Calculate position based on percentage
    const posX = (this.imagePositionX / 100) * canvas.width;
    const posY = (this.imagePositionY / 100) * canvas.height;

    ctx.save();
    ctx.translate(posX, posY);
    ctx.rotate((this.rotation * Math.PI) / 180);
    ctx.scale(this.flipH ? -1 : 1, this.flipV ? -1 : 1);
    ctx.drawImage(img, -scaledWidth / 2, -scaledHeight / 2, scaledWidth, scaledHeight);
    ctx.restore();

    ctx.filter = 'none';
    this.enhancementService.applyPixelAdjustments(ctx, canvas.width, canvas.height, this.enhancements);

    return canvas.toDataURL('image/png');
  }

  // ============ START OVER ============

  /**
   * Start over with a new image (reset everything and trigger file input)
   */
  startOver() {
    this.resetAllState();
    this.originalImage = null;
    this.correctedImage = null;
    this.triggerFileInput();
  }

  // ============ RESET ALL STATE ============

  /**
   * Reset all editor state to defaults
   * Delegates to resetToDefaults() for comprehensive reset
   */
  resetAllState() {
    this.resetToDefaults();
  }

  // ============ BRIA AI BACKGROUND GENERATION ============

  /**
   * Load Bria trial status for current user
   */
  loadBriaTrialStatus() {
    const userId = this.authService.getUserId() || 'anonymous';
    this.briaTrialStatus = this.briaAiService.getTrialStatus(userId);
  }

  /**
   * Select a Bria scene preset
   */
  selectBriaPreset(preset: { name: string; prompt: string }) {
    this.briaScenePrompt = preset.prompt;
  }

  /**
   * Generate AI background using Bria.ai
   */
  async generateAiBackground() {
    if (!this.briaScenePrompt.trim()) {
      this.briaError = this.transloco.translate('imageEditor.pleaseEnterSceneDescription');
      return;
    }

    if (!this.briaTrialStatus.isTrialActive) {
      this.briaError = this.transloco.translate('imageEditor.trialLimitReached');
      return;
    }

    // Need the corrected/processed image (with background removed)
    if (!this.correctedImage) {
      this.briaError = this.transloco.translate('imageEditor.pleaseLoadImageFirst');
      return;
    }

    this.briaGenerating = true;
    this.briaError = '';
    this.briaResultPreview = '';
    this.briaResultImage = null;

    try {
      const userId = this.authService.getUserId() || 'anonymous';

      // Get the current subject image as base64
      // We need to create a canvas with just the subject (transparent background)
      const subjectCanvas = document.createElement('canvas');
      subjectCanvas.width = this.exportAreaSize;
      subjectCanvas.height = this.exportAreaSize;
      const ctx = subjectCanvas.getContext('2d');

      if (ctx && this.correctedImage) {
        // Draw transparent background
        ctx.clearRect(0, 0, subjectCanvas.width, subjectCanvas.height);

        // Draw the subject
        const scaleX = this.imageScaleX / 100;
        const scaleY = this.imageScaleY / 100;
        const posX = (this.imagePositionX / 100) * subjectCanvas.width;
        const posY = (this.imagePositionY / 100) * subjectCanvas.height;

        ctx.save();
        ctx.translate(posX, posY);
        ctx.rotate((this.rotation * Math.PI) / 180);
        ctx.scale(this.flipH ? -1 : 1, this.flipV ? -1 : 1);

        const scaledWidth = this.correctedImage.width * scaleX;
        const scaledHeight = this.correctedImage.height * scaleY;

        ctx.drawImage(
          this.correctedImage,
          -scaledWidth / 2,
          -scaledHeight / 2,
          scaledWidth,
          scaledHeight
        );
        ctx.restore();
      }

      const imageBase64 = subjectCanvas.toDataURL('image/png');

      // Call Bria API without shadow (shadow option removed from UI)
      this.briaAiService.generateWithShadow(
        imageBase64,
        this.briaScenePrompt,
        userId,
        false, // Shadow disabled - feature removed from UI
        undefined
      ).subscribe({
        next: async (response) => {
          try {
            // Download the result image (URLs expire after 1 hour)
            const downloadedImage = await this.briaAiService.downloadImage(response.result_url);

            // Resize to match export area size (1200x1200)
            this.briaResultImage = await this.resizeImageToExportSize(downloadedImage);
            this.briaResultPreview = response.result_url;
            this.briaGenerating = false;

            // Refresh trial status
            this.loadBriaTrialStatus();
          } catch (downloadError) {
            this.briaError = this.transloco.translate('imageEditor.failedToDownloadGeneratedImage');
            this.briaGenerating = false;
          }
        },
        error: (error) => {
          this.briaError = error.message || this.transloco.translate('imageEditor.failedToGenerateBackground');
          this.briaGenerating = false;
        }
      });

    } catch (error: any) {
      this.briaError = error.message || this.transloco.translate('imageEditor.anErrorOccurred');
      this.briaGenerating = false;
    }
  }

  /**
   * Apply the generated AI background as the new background image
   */
  applyAiResult() {
    if (this.briaResultImage) {
      this.backgroundImage = this.briaResultImage;
      this.backgroundType = 'image';

      // Reset background image settings to defaults
      this.backgroundImageScale = 100;
      this.backgroundImagePositionX = 50;
      this.backgroundImagePositionY = 50;
      this.backgroundImageOpacity = 100;
      this.backgroundImageBlur = 0;
      this.backgroundImageBrightness = 100;
      this.backgroundImageContrast = 100;
      this.backgroundImageSaturation = 100;
      this.backgroundImageTemperature = 0;
      this.backgroundImageTint = 0;

      // Auto-hide subject layer since AI background already contains the product
      this.subjectLayerVisible = false;

      // Clear the preview
      this.briaResultPreview = '';
      this.briaResultImage = null;
      this.briaScenePrompt = '';

      // Close the Bria modal
      this.showBriaModal = false;

      // Apply the changes
      this.applyEnhancementsWithBackground();
    }
  }

  /**
   * Discard the generated AI background preview
   */
  discardAiResult() {
    this.briaResultPreview = '';
    this.briaResultImage = null;
  }

  /**
   * Resize an image to match the export area size (1200x1200)
   */
  private async resizeImageToExportSize(image: HTMLImageElement): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      canvas.width = this.exportAreaSize;
      canvas.height = this.exportAreaSize;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      // Draw the image scaled to fill the export area
      ctx.drawImage(image, 0, 0, this.exportAreaSize, this.exportAreaSize);

      // Convert canvas to image
      const resizedImage = new Image();
      resizedImage.onload = () => resolve(resizedImage);
      resizedImage.onerror = () => reject(new Error('Failed to load resized image'));
      resizedImage.src = canvas.toDataURL('image/png');
    });
  }

  /**
   * Scale an image to fill the frame while maintaining aspect ratio
   * Returns the scale percentage needed to fill the export area
   */
  private calculateScaleToFill(imageWidth: number, imageHeight: number): number {
    const targetSize = this.exportAreaSize;

    // Calculate the scale needed to cover the entire area
    const scaleWidth = targetSize / imageWidth;
    const scaleHeight = targetSize / imageHeight;

    // Use the larger scale to ensure the image fills the frame
    const scale = Math.max(scaleWidth, scaleHeight);

    // Convert to percentage
    return scale * 100;
  }
}
